import { baseApiClient as api } from '../baseService';

export const addTagTypes = ['events'] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      matterDomainEventsList: build.query<
        MatterDomainEventsListApiResponse,
        MatterDomainEventsListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/events/`,
          params: {
            agent: queryArg.agent,
            agent_in: queryArg.agentIn,
            correlation_id: queryArg.correlationId,
            correlation_id_in: queryArg.correlationIdIn,
            created_at_after: queryArg.createdAtAfter,
            created_at_before: queryArg.createdAtBefore,
            created_at_date: queryArg.createdAtDate,
            event_code: queryArg.eventCode,
            event_code_in: queryArg.eventCodeIn,
            event_code_not_in: queryArg.eventCodeNotIn,
            has_correlation_id: queryArg.hasCorrelationId,
            limit: queryArg.limit,
            modified_at_after: queryArg.modifiedAtAfter,
            modified_at_before: queryArg.modifiedAtBefore,
            modified_at_date: queryArg.modifiedAtDate,
            occurred_at_after: queryArg.occurredAtAfter,
            occurred_at_before: queryArg.occurredAtBefore,
            occurred_at_date: queryArg.occurredAtDate,
            offset: queryArg.offset,
            ordering: queryArg.ordering,
            payload_contains: queryArg.payloadContains,
            search: queryArg.search,
            target_id: queryArg.targetId,
            target_id_in: queryArg.targetIdIn,
            target_type: queryArg.targetType,
            this_month: queryArg.thisMonth,
            this_week: queryArg.thisWeek,
            today: queryArg.today,
            user: queryArg.user,
            user_in: queryArg.userIn,
            version: queryArg.version,
            version_gte: queryArg.versionGte,
            version_lte: queryArg.versionLte,
          },
        }),
        providesTags: ['events'],
      }),
      matterDomainEventsRetrieve: build.query<
        MatterDomainEventsRetrieveApiResponse,
        MatterDomainEventsRetrieveApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/events/${queryArg.id}/`,
        }),
        providesTags: ['events'],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as enhancedApi };
export type MatterDomainEventsListApiResponse =
  /** status 200  */ PaginatedReadDomainEventListRead;
export type MatterDomainEventsListApiArg = {
  /** Filter by agent type (can repeat parameter). */
  agent?: (
    | 'airtable_sync'
    | 'artifact_digest'
    | 'backfill_script'
    | 'bland_caller'
    | 'blob_sorter'
    | 'bpo_agent'
    | 'coverage_extractor'
    | 'deliverable_sync'
    | 'domain_event_update_orchestrator'
    | 'external_task_bulk_create'
    | 'external_task_rule_engine'
    | 'front_app_integration'
    | 'ingest_transcript_task'
    | 'loa_chaser'
    | 'loa_detector'
    | 'provider_finder'
    | 'rainmaker'
    | 'slack_task_bot'
    | 'system'
    | 'task_generator'
    | 'treatment_request_automation'
    | 'treatment_researcher'
  )[];
  /** Comma-separated list of agent types. */
  agentIn?: string;
  /** Filter by correlation ID. */
  correlationId?: string;
  /** Comma-separated list of correlation IDs. */
  correlationIdIn?: string;
  /** Events created at or after this timestamp. */
  createdAtAfter?: string;
  /** Events created at or before this timestamp. */
  createdAtBefore?: string;
  /** Events created on this calendar date. */
  createdAtDate?: string;
  /** Filter by one or more event codes (can repeat parameter). */
  eventCode?: (
    | 'allocations_replaced'
    | 'animal_created'
    | 'animal_updated'
    | 'artifact_created'
    | 'artifact_digest_created'
    | 'artifact_digest_status_updated'
    | 'blob_created'
    | 'blob_deleted'
    | 'blob_transform_completed'
    | 'correspondence_message_transmission_status_changed'
    | 'correspondence_package_approved'
    | 'correspondence_package_created'
    | 'correspondence_package_sent'
    | 'correspondence_package_status_changed'
    | 'coverage_category_changed'
    | 'coverage_created'
    | 'coverage_modification_blocked'
    | 'coverage_status_changed'
    | 'coverage_updated'
    | 'data_requirement_created'
    | 'data_requirement_satisfied'
    | 'data_requirement_status_changed'
    | 'data_requirement_value_approved'
    | 'data_requirement_value_created'
    | 'data_requirement_value_status_changed'
    | 'deliverable_created'
    | 'deliverable_match_approved'
    | 'deliverable_match_created'
    | 'deliverable_match_status_changed'
    | 'deliverable_satisfied'
    | 'deliverable_status_changed'
    | 'document_created'
    | 'document_deleted'
    | 'document_hipaa_package_generation_audit'
    | 'document_lor_generation_audit'
    | 'document_status_changed'
    | 'expense_created'
    | 'expense_deleted'
    | 'expense_updated'
    | 'external_task_rule_evaluation_created'
    | 'hipaa_authorization_signature_uploaded'
    | 'incident_created'
    | 'incident_updated'
    | 'lien_created'
    | 'lien_deleted'
    | 'lien_updated'
    | 'loa_chaser_campaign_completed'
    | 'loa_chaser_campaign_paused'
    | 'loa_chaser_campaign_resumed'
    | 'loa_chaser_campaign_started'
    | 'loa_chaser_campaign_stopped'
    | 'loa_chaser_manual_activity_logged'
    | 'loa_chaser_schedule_realigned'
    | 'loa_chaser_snoozed'
    | 'loa_chaser_step_changed'
    | 'loa_chaser_step_executed'
    | 'lor_data_updated'
    | 'matter_client_created'
    | 'matter_client_phone_number_added'
    | 'matter_client_updated'
    | 'matter_companion_members_added'
    | 'matter_companion_members_removed'
    | 'matter_companion_primary_set'
    | 'matter_created'
    | 'matter_investigation_created'
    | 'matter_investigation_updated'
    | 'matter_retainer_signed'
    | 'matter_retainer_signed_by_attorney'
    | 'matter_retainer_signed_by_client'
    | 'matter_status_changed'
    | 'matter_updated'
    | 'notebook_created'
    | 'notebook_deleted'
    | 'notebook_matter_linked'
    | 'notebook_matter_unlinked'
    | 'notebook_updated'
    | 'organization_created'
    | 'organization_updated'
    | 'person_created'
    | 'person_updated'
    | 'police_report_approval_status_changed'
    | 'police_report_created'
    | 'police_report_status_changed'
    | 'police_report_updated'
    | 'property_created'
    | 'property_updated'
    | 'provider_created'
    | 'provider_curated'
    | 'provider_phone_enriched'
    | 'provider_status_changed'
    | 'provider_treatment_status_changed'
    | 'providers_merged'
    | 'rainmaker_suggestion_executed'
    | 'staff_note_created'
    | 'staff_note_deleted'
    | 'staff_note_updated'
    | 'task_assigned'
    | 'task_created'
    | 'task_deleted'
    | 'task_external_cms_update_status_changed'
    | 'task_status_changed'
    | 'task_update_created'
    | 'task_update_deleted'
    | 'task_update_updated'
    | 'test_event'
    | 'treatment_check_in_note_logged'
    | 'treatment_check_in_submitted'
    | 'treatment_request_created'
    | 'treatment_request_deleted'
    | 'treatment_request_research_completed'
    | 'treatment_request_status_changed'
    | 'treatment_request_updated'
    | 'vehicle_created'
    | 'vehicle_updated'
    | 'voice_ai_call_completed'
    | 'voice_ai_run_unblocked'
  )[];
  /** Comma-separated list of event codes to include. */
  eventCodeIn?: string;
  /** Comma-separated list of event codes to exclude. */
  eventCodeNotIn?: string;
  /** Only return events that have any correlation ID when true. */
  hasCorrelationId?: boolean;
  /** Page size (default 50, max 1000). */
  limit?: number;
  /** Matter ID for the scoped event feed. */
  matterId: string;
  /** Events modified at or after this timestamp. */
  modifiedAtAfter?: string;
  /** Events modified at or before this timestamp. */
  modifiedAtBefore?: string;
  /** Events modified on this calendar date. */
  modifiedAtDate?: string;
  /** Events that occurred at or after this timestamp. */
  occurredAtAfter?: string;
  /** Events that occurred at or before this timestamp. */
  occurredAtBefore?: string;
  /** Events that occurred on this calendar date. */
  occurredAtDate?: string;
  /** Starting index for pagination. */
  offset?: number;
  /** Ordering clause (comma-separated). Supports fields such as occurred_at, created_at, event_code, agent, matter__name. */
  ordering?: string;
  /** Case-insensitive containment search within the JSON payload. */
  payloadContains?: string;
  /** Full-text search across event code, user, agent, matter, and firm fields. */
  search?: string;
  /** Filter by target object ID. */
  targetId?: string;
  /** Comma-separated list of target IDs. */
  targetIdIn?: string;
  /** Target content type in `app_label.model` format. */
  targetType?: string;
  /** Only events that occurred this month. */
  thisMonth?: boolean;
  /** Only events that occurred this week. */
  thisWeek?: boolean;
  /** Only events that occurred today. */
  today?: boolean;
  /** Filter by user ID. */
  user?: string;
  /** Comma-separated list of user IDs. */
  userIn?: string;
  /** Filter by exact schema version. */
  version?: number;
  /** Events with schema version greater than or equal to value. */
  versionGte?: number;
  /** Events with schema version less than or equal to value. */
  versionLte?: number;
};
export type MatterDomainEventsRetrieveApiResponse =
  /** status 200  */ ReadDomainEventRead;
export type MatterDomainEventsRetrieveApiArg = {
  id: string;
  /** Matter ID for the scoped event feed. */
  matterId: string;
  /** Domain event ID. */
  pk: string;
};
export type CustomUserSchema = {
  id: string | string;
  username: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  is_firm_admin?: boolean;
  avatar?: string | null;
  signature?: string | null;
};
export type AgentType =
  | 'ingest_transcript_task'
  | 'provider_finder'
  | 'artifact_digest'
  | 'treatment_researcher'
  | 'treatment_request_automation'
  | 'bland_caller'
  | 'slack_task_bot'
  | 'blob_sorter'
  | 'front_app_integration'
  | 'task_generator'
  | 'coverage_extractor'
  | 'airtable_sync'
  | 'system'
  | 'backfill_script'
  | 'domain_event_update_orchestrator'
  | 'loa_detector'
  | 'rainmaker'
  | 'deliverable_sync'
  | 'bpo_agent'
  | 'loa_chaser'
  | 'external_task_rule_engine'
  | 'external_task_bulk_create';
export type ProviderSummary = {
  id: string | string;
  name: string;
};
export type AllocationBucket =
  | 'CLIENT_SELF_PAYMENT'
  | 'PIP_PAYMENT'
  | 'MEDPAY_PAYMENT'
  | 'HEALTH_INSURANCE_REIMBURSEMENT'
  | 'PROVIDER_DEFERRED_PAYMENT'
  | 'UNALLOCATED';
export type PayerType = 'PROVIDER' | 'COVERAGE' | 'CLIENT';
export type CoverageCategory =
  | 'FIRST_PARTY'
  | 'FIRST_PARTY_HEALTH'
  | 'THIRD_PARTY'
  | 'NOT_LIABLE_INVOLVED_PARTY'
  | 'UNCLEAR';
export type CoverageSummary = {
  id: string | string;
  carrier_name: string | null;
  category: CoverageCategory | null;
};
export type ReadAllocation = {
  id: string | string;
  allocation_bucket: AllocationBucket;
  payer_type?: PayerType | null;
  payer_coverage?: CoverageSummary | null;
  payer_provider?: ProviderSummary | null;
  amount: string;
};
export type ReadExpense = {
  id: string | string;
  matter_id: string | string;
  provider_id: string | string;
  provider: ProviderSummary;
  service_date: string;
  billed_amount: string;
  description: string;
  related_to_incident: boolean;
  managed_by_system: string | null;
  external_id: string | null;
  allocations: ReadAllocation[];
  is_balanced: boolean;
  created_at: string;
  modified_at: string;
};
export type AllocationsReplacedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  expense: ReadExpense;
  previous_allocations: ReadAllocation[];
  new_allocations: ReadAllocation[];
};
export type ReadAllocationsReplacedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'allocations_replaced';
  version: number;
  target_type: 'app.expense';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: AllocationsReplacedEvent;
};
export type FieldSuggestionMetadata = {
  /** The field name that was accepted (e.g., 'first_name', 'ssn'). */
  field_key: string;
  /** ID of the user who accepted this field. */
  set_by_user_id?: string | string | null;
  /** Timestamp when this field was accepted. */
  set_at?: string | null;
};
export type EntitySidecar = {
  /** List of individually accepted fields. */
  field_suggestions: FieldSuggestionMetadata[];
};
export type ReadAnimal = {
  short_description?: string | null;
  involvement_description?: string | null;
  name?: string | null;
  species?: string | null;
  description?: string | null;
  id: string | string;
  matter_id: string | string;
  created_at: string;
  modified_at: string;
  approval_metadata?: EntitySidecar | null;
};
export type AnimalCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  animal: ReadAnimal;
  needs_approval: boolean;
};
export type ReadAnimalCreatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'animal_created';
  version: number;
  target_type: 'unknown.unknown';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: AnimalCreatedEvent;
};
export type AnimalUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  animal: ReadAnimal;
  previous?: ReadAnimal | null;
};
export type ReadAnimalUpdatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'animal_updated';
  version: number;
  target_type: 'unknown.unknown';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: AnimalUpdatedEvent;
};
export type ArtifactType =
  | 'TRANSCRIPT'
  | 'AUDIO_CONVERSATION'
  | 'DOCUMENT'
  | 'EMAIL'
  | 'EMAIL_ATTACHMENT'
  | 'INTAKE_NOTE'
  | 'MESSAGE'
  | 'MESSAGE_ATTACHMENT'
  | 'CONVERSATION'
  | 'BLOB'
  | 'STAFF_NOTE';
export type ArtifactPrimaryCategory =
  | 'Police Report and Evidence'
  | 'Insurance Information'
  | 'Medical Records'
  | 'Bills and Liens'
  | 'Client Correspondence'
  | 'Unclear';
export type ArtifactDigestSchema = {
  id: string | string;
  artifact_id: string | string;
  title?: string | null;
  brief?: string | null;
  primary_tag?: ArtifactPrimaryCategory | null;
  secondary_tags?: string[] | null;
  source?: string | null;
  information_score?: number | null;
  work_log_entry?: string | null;
  client_name?: string | null;
  confidence?: number | null;
  long_form_description?: string | null;
  three_line_summary?: string | null;
  one_line_summary?: string | null;
  file_name?: string | null;
  subtitle?: string | null;
  blur_by_default?: boolean | null;
  status?: string | null;
};
export type ConversationSource = 'twilio' | 'dialpad' | 'bland_ai';
export type AudioConversationSchema = {
  id: string | string;
  created_at: string;
  modified_at: string;
  name?: string | null;
  start_timestamp?: string | null;
  end_timestamp?: string | null;
  duration?: number | null;
  is_inbound?: boolean | null;
  attorney_number?: string | null;
  client_number?: string | null;
  twilio_number?: string | null;
  twilio_sid_incoming_call?: string | null;
  dialpad_call_id?: string | null;
  source?: ConversationSource | null;
};
export type TranscriptSchema = {
  id: string | string;
  transcript_path?: string | null;
  conversation?: AudioConversationSchema | null;
  created_at: string;
  modified_at: string;
};
export type BlobSchema = {
  id: string | string;
  name: string;
  original_filename: string | null;
  collection_id: string | string;
  collection_name: string;
  file_hash: string;
  file_size: number;
  file_extension: string | null;
  file_mime_type: string | null;
  download_url?: string | null;
  created_at: string;
  modified_at: string;
};
export type MessageType =
  | 'sms'
  | 'mms'
  | 'call'
  | 'custom'
  | 'email'
  | 'facebook'
  | 'front_chat'
  | 'googleplay'
  | 'intercom'
  | 'internal'
  | 'phone-call'
  | 'tweet'
  | 'tweet_dm'
  | 'whatsapp'
  | 'yalo_wha'
  | 'fax'
  | 'mailed_letter'
  | 'mailed_cheque';
export type MessageSource =
  | 'dialpad'
  | 'front'
  | 'hellosign_fax'
  | 'postgrid'
  | 'finch_correspondence';
export type MessageAttachmentSchema = {
  id: string | string;
  name: string | null;
  content_type: string | null;
  file_size: number | null;
  description: string | null;
  blob: BlobSchema | null;
};
export type RecipientRole = 'from' | 'to' | 'cc' | 'bcc' | 'reply_to';
export type BasicRecipientSchema = {
  name?: string | null;
  handle?: string | null;
  role?: RecipientRole | null;
  external_id?: string | null;
};
export type BasicRecipientBlob = {
  recipients: BasicRecipientSchema[];
};
export type TransmissionStatus =
  | 'pending'
  | 'sending'
  | 'sent'
  | 'delivered'
  | 'failed'
  | 'bounced';
export type MessageSchema = {
  id: string | string;
  message_type: MessageType;
  content: string | null;
  subject: string | null;
  blurb: string | null;
  text: string | null;
  is_inbound: boolean | null;
  from_number: string | null;
  to_numbers: string[] | null;
  source: MessageSource;
  source_timestamp: string | null;
  created_at: string;
  modified_at: string;
  attachments: MessageAttachmentSchema[];
  recipient_blob?: BasicRecipientBlob | null;
  transmission_status?: TransmissionStatus | null;
  transmission_error?: string | null;
};
export type ConversationSchema = {
  id: string | string;
  is_client_sms_conversation: boolean;
  created_at: string;
  modified_at: string;
  messages: MessageSchema[];
};
export type StaffNoteSchema = {
  content: string;
  matter_id: string | string;
  id: string | string;
  user_id: string | string;
  created_at: string;
  modified_at: string;
  agent_tags?: string[];
};
export type DetailedArtifactSchema = {
  id: string | string;
  matter_id: string | string;
  name: string;
  artifact_type: ArtifactType;
  created_at: string;
  modified_at: string;
  extract_from?: boolean;
  artifact_digest?: ArtifactDigestSchema | null;
  audio_conversation?: AudioConversationSchema | null;
  transcript?: TranscriptSchema | null;
  blob?: BlobSchema | null;
  conversation?: ConversationSchema | null;
  message?: MessageSchema | null;
  staff_note?: StaffNoteSchema | null;
};
export type ArtifactCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  artifact: DetailedArtifactSchema;
};
export type ReadArtifactCreatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'artifact_created';
  version: number;
  target_type: 'app.artifact';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: ArtifactCreatedEvent;
};
export type ArtifactDigestCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  artifact_id: string | string;
  title: string;
  status: string;
  artifact: DetailedArtifactSchema;
};
export type ReadArtifactDigestCreatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'artifact_digest_created';
  version: number;
  target_type: 'app.artifactdigest';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: ArtifactDigestCreatedEvent;
};
export type ArtifactDigestStatusUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  artifact_id: string | string;
  old_status: string;
  new_status: string;
};
export type ReadArtifactDigestStatusUpdatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'artifact_digest_status_updated';
  version: number;
  target_type: 'app.artifactdigest';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: ArtifactDigestStatusUpdatedEvent;
};
export type BlobCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  blob: BlobSchema;
};
export type ReadBlobCreatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'blob_created';
  version: number;
  target_type: 'app.blob';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: BlobCreatedEvent;
};
export type BlobDeletedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  blob: BlobSchema;
};
export type ReadBlobDeletedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'blob_deleted';
  version: number;
  target_type: 'app.blob';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: BlobDeletedEvent;
};
export type ArtifactSchema = {
  id: string | string;
  matter_id: string | string;
  name: string;
  artifact_type: ArtifactType;
  created_at: string;
  modified_at: string;
};
export type MarkdownTransformCompletedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  artifact: ArtifactSchema;
  artifact_id: string | string;
  blob_id: string | string;
};
export type ReadMarkdownTransformCompletedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'blob_transform_completed';
  version: number;
  target_type: 'app.artifact';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: MarkdownTransformCompletedEvent;
};
export type CorrespondenceMessageTransmissionStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  message_id: string | string;
  matter_id: string | string;
  old_status?: TransmissionStatus | null;
  new_status: TransmissionStatus;
  channel?: MessageType | null;
  error_message?: string | null;
};
export type ReadCorrespondenceMessageTransmissionStatusChangedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'correspondence_message_transmission_status_changed';
  version: number;
  target_type: 'app.correspondencepackage';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: CorrespondenceMessageTransmissionStatusChangedEvent;
};
export type CorrespondencePackageType =
  | 'hipaa_authorization'
  | 'foia_request'
  | 'lien_notice'
  | 'demand_package'
  | 'follow_up'
  | 'subpoena'
  | 'premises_lor'
  | 'custom';
export type CorrespondencePackageCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  package_id?: string | string | null;
  matter_id: string | string;
  package_type: CorrespondencePackageType;
  treatment_request_id?: string | string | null;
  investigation_request_id?: string | string | null;
};
export type ReadCorrespondencePackageCreatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'correspondence_package_created';
  version: number;
  target_type: 'app.correspondencepackage';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: CorrespondencePackageCreatedEvent;
};
export type ContactCardType =
  | 'medical_provider'
  | 'police_department'
  | 'insurance_carrier'
  | 'party'
  | 'custom';
export type CorrespondencePackageSentEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  package_id?: string | string | null;
  matter_id: string | string;
  message_id: string | string;
  channel: MessageType;
  entity_type?: ContactCardType | null;
  entity_id?: string | string | null;
};
export type ReadCorrespondencePackageSentEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'correspondence_package_sent';
  version: number;
  target_type: 'app.correspondencepackage';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: CorrespondencePackageSentEvent;
};
export type RenderStatus =
  | 'draft'
  | 'missing_fields'
  | 'queued_for_render'
  | 'generating'
  | 'ready'
  | 'error';
export type CorrespondencePackageStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  package_id?: string | string | null;
  matter_id: string | string;
  old_status?: RenderStatus | null;
  new_status: RenderStatus;
};
export type ReadCorrespondencePackageStatusChangedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'correspondence_package_status_changed';
  version: number;
  target_type: 'app.correspondencepackage';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: CorrespondencePackageStatusChangedEvent;
};
export type PolicyType =
  | 'AUTO'
  | 'HOMEOWNERS'
  | 'COMMERCIAL'
  | 'HEALTH'
  | 'UMBRELLA'
  | 'OTHER';
export type ContactHandleType =
  | 'PHONE'
  | 'EMAIL'
  | 'WEBSITE'
  | 'FAX'
  | 'OTHER'
  | 'MAILING_ADDRESS';
export type WebSource = {
  /** The URL of the source */
  url: string;
  /** Very short display text for the source URL */
  title: string;
};
export type ContactHandleSchema = {
  id?: string | string | null;
  /** The captured contact information. Should contain an E.164 formatted phone number, or an email address, or a website, or a fax number. */
  value?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  handle_type: ContactHandleType;
  /** Short description of the handle; e.g. 'Billing Dept Toll-Free', 'Front Desk Fax' etc. */
  name?: string | null;
  /** Optional notes about the handle. */
  notes?: string | null;
  /** Citations for the handle. */
  sources?: WebSource[] | null;
  /** Whether this is the preferred contact handle. */
  preferred?: boolean | null;
  division_id?: string | string | null;
};
export type CarrierDetail = {
  /**
            The colloquial, common form, well-known name of the insurance carrier if mentioned, null otherwise.
            This is a key field for matching and deduplication.
             */
  name?: string | null;
  /**
            The BEST (at _MOST_ 2 handles per type) web pages or phone numbers for submitting and managing claims if mentioned, null otherwise.
             */
  handles?: ContactHandleSchema[] | null;
  /**
            The headquarters address of the insurance carrier if mentioned, null otherwise.
             */
  address?: string | null;
};
export type IdentifierType =
  | 'POLICY_NUMBER'
  | 'MEMBER_ID'
  | 'GROUP_NUMBER'
  | 'OTHER';
export type PolicyIdentifier = {
  /**
            The type of identifier for the policy.
             */
  identifier_type: IdentifierType;
  /**
            The value of the identifier for the policy.
             */
  identifier_value: string;
};
export type BenefitType =
  | 'PERSONAL_INJURY_PROTECTION'
  | 'MEDICAL_PAYMENTS'
  | 'UNINSURED_MOTORIST'
  | 'UNDER_INSURED_MOTORIST'
  | 'BODILY_INJURY_LIABILITY'
  | 'PROPERTY_DAMAGE_LIABILITY'
  | 'COMPREHENSIVE_AUTO'
  | 'COLLISION_AUTO'
  | 'OTHER';
export type BenefitDetail = {
  /**
            A limited categorization of insurance benefits we are interested in extracting.
            
            <benefits-type-glossary>
                PIP == PERSONAL_INJURY_PROTECTION
                MEDPAY == MEDICAL_PAYMENTS
                UM == UNINSURED_MOTORIST
                UIM == UNDER_INSURED_MOTORIST
                BI == BODILY_INJURY_LIABILITY
                COMPREHENSIVE == COMPREHENSIVE_AUTO
                COLLISION == COLLISION_AUTO
                anything else == OTHER
            </benefits-type-glossary>
              
             */
  benefit_type?: BenefitType | null;
  /**
            Structured list of applicable coverage limitations if explicitly stated, otherwise null.
            e.g., {"per person": 100000, "per accident": 1000000}
             */
  limitations?: {
    [key: string]: number;
  } | null;
  /**
            Whether stacking benefits apply for UM/UIM coverage. When true, coverage limits from
            multiple policies can be combined. Only applicable for UNINSURED_MOTORIST and
            UNDER_INSURED_MOTORIST benefit types. Null indicates unknown/not specified.
             */
  is_stacked?: boolean | null;
};
export type CoverageDataSource = 'UNKNOWN' | 'BPO' | 'USER_EDIT' | 'SYSTEM';
export type CoverageDataSourceInfo = {
  /** The source that created or last modified this data. */
  source?: CoverageDataSource;
  /** User ID if source is USER_EDIT. */
  user_id?: string | null;
  /** User display name if source is USER_EDIT (for display purposes). */
  user_name?: string | null;
  /** When the data was added/modified from this source. */
  timestamp?: string | null;
};
export type AdjusterDetail = {
  /**
            The benefit the adjuster is specifically assigned to, if explicitly stated, otherwise null.
             */
  benefit_type?: BenefitType | null;
  /**
            Name of the adjuster handling a related claim if explicitly stated, otherwise null.
             */
  name?: string | null;
  /**
            The BEST (at MOST 2 handles per type) contact methods for the adjuster (phone, email, fax) if explicitly stated,
            otherwise null.
             */
  handles?: ContactHandleSchema[] | null;
  /**
            Metadata about the source of this adjuster data (BPO, user edit, system, etc.).
            None indicates unknown/legacy data for backwards compatibility.
             */
  source?: CoverageDataSourceInfo | null;
};
export type ClaimDetail = {
  /**
            The benefit against which the claim is being made, if explicitly stated, otherwise null.
            
            <benefits-type-glossary>
                PIP == PERSONAL_INJURY_PROTECTION
                MEDPAY == MEDICAL_PAYMENTS
                UM == UNINSURED_MOTORIST
                UIM == UNDER_INSURED_MOTORIST
                BI == BODILY_INJURY_LIABILITY
                COMPREHENSIVE == COMPREHENSIVE_AUTO
                COLLISION == COLLISION_AUTO
                anything else == OTHER
            </benefits-type-glossary>
              
             */
  benefit_type?: BenefitType | null;
  /**
            The claim identifier (number, id, etc.) for the policy, if explicitly stated, otherwise null.
             */
  claim_identifier?: string | null;
  /**
            Metadata about the source of this claim data (BPO, user edit, system, etc.).
            None indicates unknown/legacy data for backwards compatibility.
             */
  source?: CoverageDataSourceInfo | null;
};
export type VehicleDetail = {
  /**
            The make of the vehicle if explicitly stated, otherwise null.
             */
  make?: string | null;
  /**
            The model of the vehicle if explicitly stated, otherwise null.
             */
  model?: string | null;
  /**
            The year of the vehicle if explicitly stated, otherwise null.
             */
  year?: number | null;
  /**
            The VIN of the vehicle if explicitly stated, otherwise null.
             */
  vin?: string | null;
  /**
            The license plate of the vehicle if explicitly stated, otherwise null.
             */
  license_plate?: string | null;
  /**
            The color of the vehicle if explicitly stated, otherwise null.
             */
  color?: string | null;
};
export type PolicyDetail = {
  /**
            A well formatted label for the policy detail, i.e "VIN", "Vehicle Description", "Premium", "Insured Address", "Garage Address"
             */
  key: string;
  /**
            The value of the policy detail, i.e "1234567890", "Toyota Camry", "100000.00"
             */
  value: string;
};
export type SubrogatorDetail = {
  /**
            Name of the subrogator handling a related claim if explicitly stated, otherwise null.
             */
  name?: string | null;
  /**
            The BEST (at MOST 2 handles per type) contact methods for the subrogator (phone, email, fax) if explicitly stated,
            otherwise null.
             */
  handles?: ContactHandleSchema[] | null;
  /**
            List of sources that provided the subrogator information if explicitly stated, otherwise null.
             */
  sources?: WebSource[] | null;
};
export type CoverageStatus =
  | 'NEEDS_APPROVAL'
  | 'APPROVED'
  | 'REJECTED'
  | 'NOT_APPLICABLE';
export type CoverageLiabilityStatus =
  | 'ACCEPTED'
  | 'REJECTED'
  | 'PARTIALLY_ACCEPTED'
  | 'LIABILITY_UNKNOWN';
export type CoverageMentionStubSchema = {
  id: string | string;
  created_at: string;
  artifact_id: string | string;
  artifact_type: ArtifactType;
  citation_ids?: string[] | null;
  confidence?: number | null;
  context?: string | null;
};
export type EnrichmentSource =
  | 'perplexity'
  | 'cached'
  | 'manual'
  | 'intake'
  | 'extraction';
export type EnrichmentMetadata = {
  source_matter_id?: string | null;
  source_matter_name?: string | null;
  match_reason?: string | null;
  cached_at?: string | null;
  confidence_score?: number | null;
  [key: string]: any;
};
export type LorUploadedArtifactSummary = {
  id: string | string;
  blob_id?: string | string | null;
  name?: string | null;
  original_filename?: string | null;
  file_extension?: string | null;
  file_mime_type?: string | null;
  created_at?: string | null;
};
export type OpenClaimFieldStatus =
  | 'VERIFIED'
  | 'SUGGESTED'
  | 'MISSING'
  | 'UNKNOWN';
export type OpenClaimCitedFieldStr = {
  /** The discovered or existing value for this field. */
  value?: string | null;
  /** The verification status of this field. */
  status?: OpenClaimFieldStatus;
  /** Confidence score (0-1) for the accuracy of this value. */
  confidence?: number | null;
  /** Citation IDs linking to source text in documents. */
  citation_ids?: string[];
  /** Artifact ID of the primary source document. */
  source_artifact_id?: string | string | null;
  /** Brief explanation of why this value was suggested or verified. */
  reasoning?: string | null;
};
export type OpenClaimClientInfo = {
  /** Client's full legal name. */
  name?: OpenClaimCitedFieldStr;
  /** Client's contact phone number. */
  phone?: OpenClaimCitedFieldStr;
  /** Client's email address. */
  email?: OpenClaimCitedFieldStr;
  /** Client's current mailing address (street address). */
  address?: OpenClaimCitedFieldStr;
  /** Client's city. */
  city?: OpenClaimCitedFieldStr;
  /** Client's ZIP code. */
  zip_code?: OpenClaimCitedFieldStr;
  /** Client's date of birth (MM/DD/YYYY). */
  dob?: OpenClaimCitedFieldStr;
  /** Last four digits of client's Social Security Number. */
  last_four_ssn?: OpenClaimCitedFieldStr;
};
export type OpenClaimIncidentInfo = {
  /** Date the incident occurred (MM/DD/YYYY). */
  date_of_loss?: OpenClaimCitedFieldStr;
  /** Time the incident occurred (e.g., 2:30 PM or 14:30). */
  time_of_loss?: OpenClaimCitedFieldStr;
  /** Location where the incident occurred (street address or intersection). */
  location?: OpenClaimCitedFieldStr;
  /** Weather conditions at time of incident. */
  weather_conditions?: OpenClaimCitedFieldStr;
  /** Brief description of how the incident occurred. */
  incident_description?: OpenClaimCitedFieldStr;
};
export type OpenClaimCitedFieldUnionIntNoneType = {
  /** The discovered or existing value for this field. */
  value?: number | null;
  /** The verification status of this field. */
  status?: OpenClaimFieldStatus;
  /** Confidence score (0-1) for the accuracy of this value. */
  confidence?: number | null;
  /** Citation IDs linking to source text in documents. */
  citation_ids?: string[];
  /** Artifact ID of the primary source document. */
  source_artifact_id?: string | string | null;
  /** Brief explanation of why this value was suggested or verified. */
  reasoning?: string | null;
};
export type OpenClaimVehicleInfo = {
  /** Vehicle model year. */
  year?: OpenClaimCitedFieldUnionIntNoneType;
  /** Vehicle manufacturer (e.g., Toyota, Ford). */
  make?: OpenClaimCitedFieldStr;
  /** Vehicle model name (e.g., Camry, F-150). */
  model?: OpenClaimCitedFieldStr;
  /** Vehicle Identification Number. */
  vin?: OpenClaimCitedFieldStr;
  /** License plate number and state. */
  license_plate?: OpenClaimCitedFieldStr;
  /** Vehicle color. */
  color?: OpenClaimCitedFieldStr;
  /** UUID of the coverage this vehicle should be assigned to. */
  suggested_coverage_id?: OpenClaimCitedFieldStr;
};
export type OpenClaimCoverageInfo = {
  /** Name of the insurance carrier. */
  carrier_name?: OpenClaimCitedFieldStr;
  /** Policy number on the insurance card/documents. */
  policy_number?: OpenClaimCitedFieldStr;
  /** Claims phone number for the carrier. */
  carrier_phone?: OpenClaimCitedFieldStr;
  /** Name of the policyholder/named insured. */
  named_insured?: OpenClaimCitedFieldStr;
  /** Existing claim number if a claim has already been filed. */
  claim_number?: OpenClaimCitedFieldStr;
};
export type OpenClaimAtFaultDriverInfo = {
  /** Name of the at-fault or other involved driver. */
  name?: OpenClaimCitedFieldStr;
  /** Phone number of the at-fault driver. */
  phone?: OpenClaimCitedFieldStr;
  /** Address of the at-fault driver. */
  address?: OpenClaimCitedFieldStr;
};
export type OpenClaimContextSchema = {
  /** Client/claimant information. */
  client?: OpenClaimClientInfo;
  /** Details about the incident/accident. */
  incident?: OpenClaimIncidentInfo;
  /** Client's vehicle(s) involved in the incident. */
  client_vehicles?: OpenClaimVehicleInfo[];
  /** Insurance coverage details. */
  coverage?: OpenClaimCoverageInfo;
  /** At-fault or other party driver information. */
  at_fault_driver?: OpenClaimAtFaultDriverInfo;
  /** At-fault or other party vehicle information. */
  defendant_vehicle?: OpenClaimVehicleInfo;
  /** ISO timestamp when AI discovery completed. */
  discovery_completed_at?: string | null;
  /** Summary of what was found and what's missing. */
  discovery_summary?: string | null;
};
export type CoverageSchema = {
  /**
            The type of policy if explicitly stated, otherwise null.
             */
  policy_type?: PolicyType | null;
  /**
            Details about the insurance carrier if mentioned, otherwise null.
             */
  carrier?: CarrierDetail | null;
  /**
            Carrier-issued identifiers for the policy, if explicitly stated, otherwise null.
             */
  policy_identifiers?: PolicyIdentifier[] | null;
  /**
            List of names of primary policyholders or insured entities if stated or implied, otherwise null.
             */
  named_insured?: string[] | null;
  /**
            List of benefits covered by the policy, if explicitly stated, otherwise null.
             */
  benefits?: BenefitDetail[] | null;
  /**
            Any known adjusters handling claims against this policy, if explicitly stated, otherwise null.
             */
  adjusters?: AdjusterDetail[] | null;
  /**
            Any known claims opened against the policy, if explicitly stated, otherwise null.
             */
  claims?: ClaimDetail[] | null;
  /**
            List of vehicles covered by the policy, if applicable and explicitly stated, otherwise null.
             */
  vehicles?: VehicleDetail[] | null;
  /**
            Structured list of extracted policy details, such as, but not limited to:
            VIN; vehicle description; premium amount; insured's address;
            garage location; policy endorsements; coverage exclusions;
            other policy-specific information for claims processing.
             */
  policy_details?: PolicyDetail[] | null;
  /**
            Details about the subrogator if health insurance policy + carrier ... if explicitly stated, otherwise null.
             */
  subrogator?: SubrogatorDetail | null;
  id: string | string;
  status?: CoverageStatus | null;
  liability_status?: CoverageLiabilityStatus | null;
  category?: CoverageCategory | null;
  matter_id: string | string;
  created_at: string;
  modified_at: string;
  /**
            List of mentions of this coverage in the matter, if any.
             */
  mentions?: CoverageMentionStubSchema[] | null;
  carrier_v2_id?: string | string | null;
  carrier_division_id?: string | string | null;
  /** Source of enrichment data for carrier information */
  enrichment_source?: EnrichmentSource | null;
  /** Metadata about enrichment source (source matter ID, match reason, etc.) */
  enrichment_metadata?: EnrichmentMetadata | null;
  /** Whether cached data was used for enrichment */
  cache_used?: boolean;
  lor_document_id?: string | string | null;
  lor_uploaded_artifact_id?: string | string | null;
  lor_uploaded_artifact?: LorUploadedArtifactSummary | null;
  /** AI-discovered context for opening a claim with citations and confidence scores. */
  open_claim_context?: OpenClaimContextSchema | null;
};
export type CoverageCategoryChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  coverage: CoverageSchema;
  old_category: CoverageCategory | null;
  new_category: CoverageCategory | null;
};
export type ReadCoverageCategoryChangedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'coverage_category_changed';
  version: number;
  target_type: 'app.coverage';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: CoverageCategoryChangedEvent;
};
export type CoverageCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  coverage: CoverageSchema;
  needs_approval: boolean;
};
export type ReadCoverageCreatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'coverage_created';
  version: number;
  target_type: 'app.coverage';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: CoverageCreatedEvent;
};
export type CoverageModificationBlockedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  coverage: CoverageSchema;
  operation_type: string;
  workflow_type: string;
  blocked_reason: string;
  recommendation?: {
    [key: string]: any;
  } | null;
};
export type ReadCoverageModificationBlockedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'coverage_modification_blocked';
  version: number;
  target_type: 'app.coverage';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: CoverageModificationBlockedEvent;
};
export type CoverageStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  coverage: CoverageSchema;
  old_status: CoverageStatus;
  new_status: CoverageStatus;
};
export type ReadCoverageStatusChangedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'coverage_status_changed';
  version: number;
  target_type: 'app.coverage';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: CoverageStatusChangedEvent;
};
export type CoverageUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  coverage: CoverageSchema;
  diff: {
    [key: string]: any;
  };
  previous?: CoverageSchema | null;
};
export type ReadCoverageUpdatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'coverage_updated';
  version: number;
  target_type: 'app.coverage';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: CoverageUpdatedEvent;
};
export type DataRequirementName =
  | 'client_name'
  | 'client_date_of_birth'
  | 'client_last_four_of_ssn'
  | 'client_address'
  | 'firm_full_name'
  | 'date_of_loss'
  | 'incident_location'
  | 'matter_city'
  | 'matter_state'
  | 'sanitized_incident_description'
  | 'insurance_carrier'
  | 'insurance_policy_number'
  | 'insurance_claim_number'
  | 'insurance_adjuster_name'
  | 'other_driver_name'
  | 'other_driver_insurance_carrier'
  | 'other_driver_insurance_policy_number'
  | 'medical_provider_name'
  | 'medical_treatment_date';
export type DataRequirementValidationCriteria = {
  expected_format?: string | null;
  validation_prompt?: string | null;
  required_fields?: string[] | null;
};
export type CanonicalModel =
  | 'matter'
  | 'client'
  | 'firm'
  | 'insurance_coverage'
  | 'medical_provider'
  | 'opposing_party';
export type DataRequirementStatus = 'Pending' | 'Satisfied' | 'Bypassed';
export type DataRequirementAgentContext = {
  rationale?: string | null;
};
export type DataRequirementValueStatus = 'Extracted' | 'Approved' | 'Rejected';
export type DataRequirementValueContext = {
  rationale?: string | null;
  extraction_source?: string | null;
  artifact_id?: string | string | null;
  confidence_notes?: string | null;
  /** Up to three verbatim snippets from the text that support the extracted value. Include enough leading context to be self-explanatory.  */
  excerpts?: string[];
  /**
            Short, comprehensive sentence, succinctly describing where this data was found in the document.
             */
  extraction_context?: string | null;
  /**
            If there are "citation-id" blocks in the text that correspond to the excerpts, list the IDs here.
             */
  citation_ids?: string[] | null;
};
export type DataRequirementValueSchema = {
  value: string;
  status: DataRequirementValueStatus;
  confidence?: number | null;
  context?: DataRequirementValueContext | null;
  id: string | string;
  data_requirement_id: string | string;
  created_at: string;
  modified_at: string;
  user_id?: string | string | null;
  agent?: AgentType | null;
};
export type DataRequirementSchema = {
  name: DataRequirementName;
  display_name: string;
  description?: string | null;
  prompt: string;
  validation_criteria?: DataRequirementValidationCriteria | null;
  canonical_model?: CanonicalModel | null;
  canonical_field?: string | null;
  status?: DataRequirementStatus | null;
  id: string | string;
  task_id: string | string;
  created_at: string;
  modified_at: string;
  user_id?: string | string | null;
  agent?: AgentType | null;
  agent_context?: DataRequirementAgentContext | null;
  values?: DataRequirementValueSchema[];
  canonical_value?: string | null;
};
export type DataRequirementCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  data_requirement: DataRequirementSchema;
  task_id: string | string;
  name: string;
  display_name: string;
};
export type ReadDataRequirementCreatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'data_requirement_created';
  version: number;
  target_type: 'app.datarequirement';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: DataRequirementCreatedEvent;
};
export type DataRequirementSatisfiedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  data_requirement: DataRequirementSchema;
  task_id: string | string;
  satisfied_by_value_id: string | string | null;
};
export type ReadDataRequirementSatisfiedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'data_requirement_satisfied';
  version: number;
  target_type: 'app.datarequirement';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: DataRequirementSatisfiedEvent;
};
export type DataRequirementStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  data_requirement: DataRequirementSchema;
  old_status: DataRequirementStatus | null;
  new_status: DataRequirementStatus;
};
export type ReadDataRequirementStatusChangedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'data_requirement_status_changed';
  version: number;
  target_type: 'app.datarequirement';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: DataRequirementStatusChangedEvent;
};
export type DataRequirementValueApprovedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  data_requirement_value: DataRequirementValueSchema;
  data_requirement_id: string | string;
  value: string;
};
export type ReadDataRequirementValueApprovedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'data_requirement_value_approved';
  version: number;
  target_type: 'app.datarequirementvalue';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: DataRequirementValueApprovedEvent;
};
export type DataRequirementValueCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  data_requirement_value: DataRequirementValueSchema;
  data_requirement_id: string | string;
  value: string;
  status: DataRequirementValueStatus;
};
export type ReadDataRequirementValueCreatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'data_requirement_value_created';
  version: number;
  target_type: 'app.datarequirementvalue';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: DataRequirementValueCreatedEvent;
};
export type DataRequirementValueStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  data_requirement_value: DataRequirementValueSchema;
  data_requirement_id: string | string;
  old_status: DataRequirementValueStatus | null;
  new_status: DataRequirementValueStatus;
};
export type ReadDataRequirementValueStatusChangedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'data_requirement_value_status_changed';
  version: number;
  target_type: 'app.datarequirementvalue';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: DataRequirementValueStatusChangedEvent;
};
export type DeliverableArtifactCriteria = {
  search_keywords?: string[] | null;
  exclusion_keywords?: string[] | null;
};
export type DeliverableAnchors = {
  facts?: {
    [key: string]: string;
  };
};
export type DeliverableStatus = 'Pending' | 'Satisfied' | 'Bypassed';
export type TaskGroups =
  | 'Intake'
  | 'Police Report & Investigation'
  | '1P Insurance'
  | '3P Insurance'
  | 'Medical Records'
  | 'Treatment'
  | 'Demand Letter'
  | 'Liens'
  | 'Miscellaneous'
  | 'Additional Insurance';
export type TaskType =
  | 'Medical Records'
  | 'Medical Bills'
  | 'LOR Acknowledgement'
  | 'Send LOR'
  | 'Open Claim'
  | 'Final Lien'
  | 'Police Report'
  | 'Police Report Escalation'
  | 'Freedom of Information Act'
  | 'Incident Report'
  | 'Open File'
  | 'Locate Report Source'
  | 'Ascertain Subrogator'
  | 'Finalize Demand'
  | 'Draft Demand'
  | 'Declaration Page'
  | 'Health Insurance Card'
  | 'Treatment Check-In'
  | 'Treatment Barrier'
  | 'HIPAA Directive'
  | 'Retainer Agreement'
  | 'Conduct Intake'
  | 'Conduct Initial Review'
  | 'Welcome Call'
  | 'Other'
  | 'All'
  | 'Review Found Coverage'
  | 'Review Found Provider'
  | 'Update External CMS'
  | 'Find 1P Coverage'
  | 'Find 3P Coverage'
  | 'Client Communications'
  | 'Managing Paralegal Asks'
  | 'Manage MedPay'
  | 'Send MedPay Letter'
  | 'Get Liability Limits'
  | 'Request Updated PIP Ledger'
  | 'Send Evidence Preservation Letter'
  | 'Document Collection'
  | 'Review 1P Benefits'
  | 'PIP/MedPay Application'
  | 'Check for PIP Ledger'
  | 'IME Required'
  | 'UM/UIM Indicate Stack vs. No Stack'
  | 'Generate No Medpay Letter'
  | 'Provide PIP Info to Medical Provider'
  | 'Liability Other'
  | '1P Insurance Other'
  | '3P Insurance Other'
  | 'Liens Other'
  | 'Treatment Other'
  | 'Medical Records Other'
  | 'Demand Letter Other';
export type TaskUpdateType = 'comment' | 'activity';
export type TaskUpdateVisibility = 'Private' | 'Public';
export type TaskUpdateSchema = {
  id: string | string;
  task_id: string | string;
  update: string;
  update_type: TaskUpdateType;
  visibility: TaskUpdateVisibility;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  created_at: string;
  modified_at: string;
};
export type TaskExternalCmsUpdateStatus = 'NOT_UPDATED' | 'UPDATED';
export type TaskWithUpdatesSchema = {
  id: string | string;
  name?: string | null;
  description?: string | null;
  group: TaskGroups;
  type: TaskType;
  due_date?: string | null;
  updates: TaskUpdateSchema[];
  external_cms_update_status?: TaskExternalCmsUpdateStatus;
  external_cms_update_reason?: string | null;
};
export type FirmStatus =
  | 'Active'
  | 'Testing'
  | 'Software Only'
  | 'New Lead'
  | 'Open'
  | 'Closed';
export type FirmType = 'Solo' | 'Small' | 'Medium' | 'Enterprise';
export type TimeZone = 'PST' | 'MST' | 'CST' | 'EST' | 'AKST' | 'AST' | 'HST';
export type CmsSystem =
  | 'CASEpeer'
  | 'Filevine'
  | 'Litify'
  | 'MyCase'
  | 'Clio Manage'
  | 'SmartAdvocate'
  | 'PracticePanther'
  | 'Needles'
  | 'Neos'
  | 'Merus'
  | 'TrialWorks';
export type LeadSystem =
  | 'LeadDocket'
  | 'LawProNation'
  | 'ClioGrow'
  | 'Intaker'
  | 'CASEpeer'
  | 'Filevine'
  | 'Litify'
  | 'MyCase'
  | 'Clio Manage'
  | 'SmartAdvocate'
  | 'PracticePanther'
  | 'Needles'
  | 'Neos'
  | 'Merus'
  | 'TrialWorks';
export type IntakePreferenceSection = {
  title: string;
  content: string;
};
export type QualificationAction = 'accept' | 'reject' | 'escalate';
export type QualificationCriteria = {
  /** Maximum days from today for date of loss. None means no restriction. */
  date_of_loss_max_days?: number | null;
  /** List of 2-letter US state codes. Empty means all states accepted. */
  accepted_states?: string[];
  /** Per case type actions. Missing types use default action. */
  case_type_actions?: {
    [key: string]: QualificationAction;
  };
  /** Default action for unconfigured case types. */
  default_case_type_action?: QualificationAction;
};
export type IntakePreferences = {
  sections?: IntakePreferenceSection[];
  /** Qualification criteria for intake decision support. */
  qualification_criteria?: QualificationCriteria | null;
};
export type FirmSchema = {
  id: string;
  name: string;
  full_name?: string | null;
  email?: string | null;
  adobe_sign_email?: string | null;
  status?: FirmStatus | null;
  type?: FirmType | null;
  is_paying?: boolean;
  paying_since?: string | null;
  time_zone?: TimeZone | null;
  primary_state?: string | null;
  managing_paralegal_id?: string | string | null;
  cms_system?: CmsSystem | null;
  cms_link?: string | null;
  lead_system?: LeadSystem | null;
  lead_system_link?: string | null;
  intake_preferences: IntakePreferences;
  case_preferences?: string | null;
  commentary?: string | null;
  errors?: string | null;
  plan_to_derisk?: string | null;
  pre_filing_contingency_fee?: string | null;
  post_filing_contingency_fee?: string | null;
  sms_sender_number?: string | null;
  sms_sender_name?: string | null;
};
export type MatterType =
  | 'Motor Vehicle Accident'
  | 'Medical Malpractice'
  | 'Miscellaneous'
  | 'Breach of Employment Contract'
  | 'Seller Non-Disclosure'
  | 'Premises Liability'
  | 'Broker Negligence'
  | 'Unknown';
export type MatterStatus =
  | 'New Lead'
  | 'Intake'
  | 'Initial Review'
  | 'Case Setup'
  | 'Treatment'
  | 'Treatment Complete'
  | 'Ready for Demand'
  | 'Demand Preparation'
  | 'Demand Letter'
  | 'Complete'
  | 'Hidden'
  | 'In Progress'
  | 'Client Dropped'
  | 'To Be Dropped';
export type MatterServices =
  | 'Screening & Retainer'
  | 'Intake Call'
  | 'Case Setup'
  | 'Medical Records'
  | 'Demand';
export type ReadMatterSchema = {
  id: string | string;
  name?: string | null;
  matter_type?: MatterType;
  status?: MatterStatus | null;
  date_of_loss?: string | null;
  statute_of_limitations_date?: string | null;
  services?: MatterServices[];
  city?: string | null;
  state?: string | null;
  created_at?: string | null;
  modified_at?: string | null;
  representing_attorney_id?: string | string | null;
  referral_source_description?: string | null;
};
export type AddressUpdateModel = {
  line1?: string | null;
  line2?: string | null;
  line3?: string | null;
  city?: string | null;
  state?: string | null;
  postcode?: string | null;
  country?:
    | (
        | 'AF'
        | 'AX'
        | 'AL'
        | 'DZ'
        | 'AS'
        | 'AD'
        | 'AO'
        | 'AI'
        | 'AQ'
        | 'AG'
        | 'AR'
        | 'AM'
        | 'AW'
        | 'AU'
        | 'AT'
        | 'AZ'
        | 'BS'
        | 'BH'
        | 'BD'
        | 'BB'
        | 'BY'
        | 'BE'
        | 'BZ'
        | 'BJ'
        | 'BM'
        | 'BT'
        | 'BO'
        | 'BQ'
        | 'BA'
        | 'BW'
        | 'BV'
        | 'BR'
        | 'IO'
        | 'BN'
        | 'BG'
        | 'BF'
        | 'BI'
        | 'CV'
        | 'KH'
        | 'CM'
        | 'CA'
        | 'KY'
        | 'CF'
        | 'TD'
        | 'CL'
        | 'CN'
        | 'CX'
        | 'CC'
        | 'CO'
        | 'KM'
        | 'CG'
        | 'CD'
        | 'CK'
        | 'CR'
        | 'CI'
        | 'HR'
        | 'CU'
        | 'CW'
        | 'CY'
        | 'CZ'
        | 'DK'
        | 'DJ'
        | 'DM'
        | 'DO'
        | 'EC'
        | 'EG'
        | 'SV'
        | 'GQ'
        | 'ER'
        | 'EE'
        | 'SZ'
        | 'ET'
        | 'FK'
        | 'FO'
        | 'FJ'
        | 'FI'
        | 'FR'
        | 'GF'
        | 'PF'
        | 'TF'
        | 'GA'
        | 'GM'
        | 'GE'
        | 'DE'
        | 'GH'
        | 'GI'
        | 'GR'
        | 'GL'
        | 'GD'
        | 'GP'
        | 'GU'
        | 'GT'
        | 'GG'
        | 'GN'
        | 'GW'
        | 'GY'
        | 'HT'
        | 'HM'
        | 'VA'
        | 'HN'
        | 'HK'
        | 'HU'
        | 'IS'
        | 'IN'
        | 'ID'
        | 'IR'
        | 'IQ'
        | 'IE'
        | 'IM'
        | 'IL'
        | 'IT'
        | 'JM'
        | 'JP'
        | 'JE'
        | 'JO'
        | 'KZ'
        | 'KE'
        | 'KI'
        | 'KP'
        | 'KR'
        | 'KW'
        | 'KG'
        | 'LA'
        | 'LV'
        | 'LB'
        | 'LS'
        | 'LR'
        | 'LY'
        | 'LI'
        | 'LT'
        | 'LU'
        | 'MO'
        | 'MG'
        | 'MW'
        | 'MY'
        | 'MV'
        | 'ML'
        | 'MT'
        | 'MH'
        | 'MQ'
        | 'MR'
        | 'MU'
        | 'YT'
        | 'MX'
        | 'FM'
        | 'MD'
        | 'MC'
        | 'MN'
        | 'ME'
        | 'MS'
        | 'MA'
        | 'MZ'
        | 'MM'
        | 'NA'
        | 'NR'
        | 'NP'
        | 'NL'
        | 'NC'
        | 'NZ'
        | 'NI'
        | 'NE'
        | 'NG'
        | 'NU'
        | 'NF'
        | 'MK'
        | 'MP'
        | 'NO'
        | 'OM'
        | 'PK'
        | 'PW'
        | 'PS'
        | 'PA'
        | 'PG'
        | 'PY'
        | 'PE'
        | 'PH'
        | 'PN'
        | 'PL'
        | 'PT'
        | 'PR'
        | 'QA'
        | 'RE'
        | 'RO'
        | 'RU'
        | 'RW'
        | 'BL'
        | 'SH'
        | 'KN'
        | 'LC'
        | 'MF'
        | 'PM'
        | 'VC'
        | 'WS'
        | 'SM'
        | 'ST'
        | 'SA'
        | 'SN'
        | 'RS'
        | 'SC'
        | 'SL'
        | 'SG'
        | 'SX'
        | 'SK'
        | 'SI'
        | 'SB'
        | 'SO'
        | 'ZA'
        | 'GS'
        | 'SS'
        | 'ES'
        | 'LK'
        | 'SD'
        | 'SR'
        | 'SJ'
        | 'SE'
        | 'CH'
        | 'SY'
        | 'TW'
        | 'TJ'
        | 'TZ'
        | 'TH'
        | 'TL'
        | 'TG'
        | 'TK'
        | 'TO'
        | 'TT'
        | 'TN'
        | 'TR'
        | 'TM'
        | 'TC'
        | 'TV'
        | 'UG'
        | 'UA'
        | 'AE'
        | 'GB'
        | 'US'
        | 'UM'
        | 'UY'
        | 'UZ'
        | 'VU'
        | 'VE'
        | 'VN'
        | 'VG'
        | 'VI'
        | 'WF'
        | 'EH'
        | 'YE'
        | 'ZM'
        | 'ZW'
      )
    | null;
};
export type MatterClientPhoneNumberSchema = {
  phone_number: string;
};
export type MatterClientSchema = {
  id?: string | string | null;
  name?: string | null;
  email?: string | null;
  date_of_birth?: string | null;
  last_four_of_ssn?: string | null;
  preferred_language?: string | null;
  address?: AddressUpdateModel | null;
  phone_numbers?: MatterClientPhoneNumberSchema[];
};
export type ProviderStatus = 'NEEDS_APPROVAL' | 'APPROVED' | 'REJECTED';
export type ProviderTreatmentStatus =
  | 'TREATING'
  | 'COMPLETED'
  | 'DEVIATING'
  | 'UNKNOWN';
export type ProviderType = 'ORGANIZATION' | 'PRACTITIONER';
export type ProviderSearchResult = {
  /** Name of the practitioner (e.g. 'Dr. John Smith') if explicitly stated, null otherwise. */
  practitioner_name?: string | null;
  /** Common, recognizable name of the healthcare organization (e.g., 'UC San Diego Health', 'Main Street Clinic'). EXCLUDE administrative department names (e.g., 'Medical Records', 'Billing'); these belong in handles. Align with original mention's generality unless search strongly indicates a specific, distinct facility. */
  organization_name?: string | null;
  /** The address of the provider. */
  address?: string | null;
  /** The BEST (at MOST 2 handles per type) contact methods for records/billing retrieval. Do not include scheduling phone numbers, or other patient care oriented phone numbers. */
  handles: ContactHandleSchema[];
  /** A very brief (1-2 sentences) note for paralegal context. */
  internal_note?: string | null;
};
export type EncounterSchema = {
  /** The unique identifier for the encounter */
  id: string;
  /** The date of service for the encounter in ISO format (YYYY-MM-DD), if explicitly stated, null otherwise */
  date?: string | null;
  /** list of short phrases describing care provided in the encounter as mentioned in the artifact, like 'physical therapy', 'labs', 'imaging', etc. if explicitly stated, null otherwise */
  services?: string[];
  /** A very concise description of the encounter, as a one liner that might be used in a medical chronology for the case. Don't repeat the date of service in the description . Explicitly mention any ambiguities about the discovery that merit further investigation (i.e date of service not yet known, etc.) */
  description: string;
};
export type TreatmentFrequency =
  | 'DAILY'
  | 'WEEKLY'
  | 'BI_WEEKLY'
  | 'MONTHLY'
  | 'AS_NEEDED'
  | 'OTHER';
export type TreatmentProgress = 'IMPROVING' | 'STAGNANT' | 'REGRESSING';
export type TreatmentCareLevel =
  | 'EMERGENCY_CARE'
  | 'CONSERVATIVE_CARE'
  | 'DIAGNOSTIC_TESTING'
  | 'SPECIALIST_CARE'
  | 'SURGICAL_CARE';
export type MedicalProviderSchema = {
  id: string;
  matter_id: string;
  provider_contact_ref_id?: string | null;
  name: string;
  status?: ProviderStatus | null;
  dates_of_service?: string | null;
  treatment?: string | null;
  address?: string | null;
  phone?: string | null;
  fax?: string | null;
  email?: string | null;
  still_treating?: string | null;
  treatment_status: ProviderTreatmentStatus;
  treatment_status_occurred_at?: string | null;
  date_of_records_request?: string | null;
  provider_type?: ProviderType | null;
  practitioner_names?: string[] | null;
  description?: string | null;
  handles: ContactHandleSchema[];
  inference_reasoning?: string | null;
  fallback_leads?: ProviderSearchResult[] | null;
  encounters?: EncounterSchema[] | null;
  pain_level?: number | null;
  treatment_notes?: string | null;
  treatment_frequency?: TreatmentFrequency | null;
  treatment_progress?: TreatmentProgress | null;
  care_level?: TreatmentCareLevel | null;
};
export type PoliceReportAttestationStatus =
  | 'rejected_verified'
  | 'unclear'
  | 'inferred'
  | 'attested_unverified'
  | 'attested_verified'
  | 'verified_received';
export type PoliceReportAvailabilityStatus =
  | 'verified_ready'
  | 'verified_pending'
  | 'unknown'
  | 'available'
  | 'not_available';
export type OfficerDetail = {
  name: string;
  badge_number?: string | null;
  context?: string | null;
};
export type PoliceReportEscalationReason =
  | 'cant_verify_attested_report'
  | 'verified_ready_but_no_automation_available'
  | 'verified_but_unclear_if_ready'
  | 'found_on_portal_but_failed_to_retrieve';
export type JurisdictionType =
  | 'city'
  | 'county'
  | 'state'
  | 'federal'
  | 'tribal'
  | 'special';
export type PoliceDepartmentEnrichmentStatus =
  | 'not_enriched'
  | 'enrichment_in_progress'
  | 'enrichment_failed'
  | 'enrichment_succeeded';
export type ServiceType =
  | 'crash_reports'
  | 'foia_equivalent'
  | 'criminal_records'
  | 'incident_reports'
  | 'general_records';
export type PoliceRecordsInquiryMajorVendor =
  | 'carfax_crashdocs'
  | 'lexis_nexis_buycrash'
  | 'independent_portal'
  | 'next_request'
  | 'gov_qa'
  | 'not_a_portal'
  | 'txdot_cris';
export type AddressSchema = {
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  province_or_state?: string | null;
  postal_or_zip?: string | null;
  country_code?: string | null;
};
export type MailRequirement =
  | 'CLIENT_PHOTO_ID_COPY'
  | 'FIRM_LETTERHEAD'
  | 'PROOF_OF_REPRESENTATION'
  | 'COMPLETED_REQUEST_FORM'
  | 'NOTARIZED_REQUEST_FORM';
export type FeeComponent = {
  /** The amount of the fee in cents. */
  amount_cents: number;
  /** One line description of the component of the total fee backed by research and assumptions.
    ex:
    - "Upper bound estimate of 20 pages at $0.25/page as specified by ____"
    - "Estimating up to 30min of labor at stated rate of $20/hour as specified by ____"
    - "Base certified crash report fee $8.44 as specified by ____"
    - "Added 7% for tax because site mentions it's not included in provided info" */
  description: string;
};
export type RecordsFeeEstimate = {
  /** An estimated fee for requesting a copy of a police report.
    Depending on the department, sometimes this is called a "crash report", "accident report",
    "incident report", or "police report".
    
    Rule: IF more than one report option is available, bias to the more expensive, or certified option.
    If fees are not defined for the department, fall back to county or state guidance on fees. */
  estimated_fee_cents?: number | null;
  /** All the components that led to the estimated fee */
  components?: FeeComponent[] | null;
  /** Full verbatim fee details including:
    - Exact language from official source
    - Conditions, alternatives, or special cases
    - Statutory citations (e.g., "per RSMo §610.026")
    - Payment instructions
    ALWAYS populate this when ANY fee information is available. */
  notes?: string | null;
  /** A 0-1 score indicating the confidence. */
  confidence_score?: number | null;
  /** 2-3 sentences explaining the confidence score. */
  reasoning?: string | null;
};
export type CheckMailingInstructions = {
  /** Verifiable instructions on how to request a police report from the agency by mail. */
  instructions?: string;
  /** The mailing address to send the request to. */
  address: AddressSchema;
  /** The best, single name of the entity to make the check payable to. (Payee)
    Do not place anything except for a single, bank-ready name here. */
  payable_to?: string | null;
  /** Any specific text required to be written on the check memo line, if explicitly mentioned, otherwise null.
    Example: "Police Report Request" or "Report #[REPORT_NUMBER]" or "Accident Report Fee"
    available variables: "[REPORT_NUMBER], [CLIENT_NAME], [DATE_OF_LOSS]" */
  memo_instructions?: string | null;
  /** The number of days after incident date to wait before requesting
    the records by mail, if explicitly specified, otherwise null. */
  wait_before_requesting_days?: number | null;
  /** Any department-specified, or state-specified, number of days to expect for processing, if explicitly stated, otherwise null. */
  estimated_processing_days?: number | null;
  /** Any items that are explicitly REQUIRED for requesting a police report by mail, if described, otherwise null. */
  required_items?: MailRequirement[] | null;
  /** Any special requirements for requesting a police report by mail, if described, otherwise null. */
  special_requirements?: string | null;
  /** The estimated set of fees to request a copy of a police report by mail.
    
    - (for any question of the report type being sought): assume we want the most detailed version of the report, sometimes this is "crash report", "accident report", "incident report", "police report", bias to the more expensive of the four
    - (for any page-based fee structure) assume a 20 page incident report
    - (for any requestor-based fee differences) assume legal representative of involved party
    - (for request-method based pricing) assume check + mail but bias to more expensive option
    - (for any charge for a missing report number) assume we do not have the report number
    - (for any charges related to labor) assume 30 minutes of labor
    - (for any charges related to certified copies) assume we are paying for the most certified copy */
  fee_structure?: RecordsFeeEstimate | null;
  /** Web pages that were used to gather the information. */
  citations?: WebSource[] | null;
};
export type DepartmentForm = {
  /** Concise title for the Form */
  title: string;
  /** Concise sentence description of the Form and its purpose. */
  description?: string | null;
  /** URL of the Form file */
  file_url: string;
};
export type ReadDepartmentRecordsChannel = {
  /** A concise title for the channel - e.g. "FOIA Portal", "Records Department Phone" */
  title?: string | null;
  /** Single sentence description of the channel and its purpose. */
  description?: string | null;
  /** Tags for record types that can be serviced by this channel.
    e.g. ["crash_reports", "foia_equivalent", "general_records"] */
  services_provided?: ServiceType[] | null;
  /** The type of channel: mail, phone, email, online_portal, fax */
  channel_type?: string | null;
  /** Web sources/citations for this channel information */
  citations?: WebSource[] | null;
  /** Concise instructions on how to request records from the police department, if explicitly mentioned, otherwise null.
    e.g. 'To request a police report, please visit the police department website and fill out the request form.' */
  instructions: string;
  handle: ContactHandleSchema;
  fee_amount?: number | null;
  major_vendor?: PoliceRecordsInquiryMajorVendor | null;
  /** An explicitly provided link to a pdf, docx, or other static file form that the department publishes and requires
    inquirers to fill out for making police report requests. Not every PD may ask for inquirers to provide a formal request,
    but if they do, we should capture them here - otherwise null. */
  required_document_url?: WebSource | null;
  mailing_instructions?: CheckMailingInstructions | null;
  /** Department-provided forms associated with this channel */
  forms?: DepartmentForm[] | null;
  /** Portal URL if this is an online portal channel */
  portal_url?: string | null;
  /** Portal domain if this is an online portal channel */
  portal_domain?: string | null;
  id: string | string;
  created_at: string;
  modified_at: string;
  police_department_id: string | string;
  required_document_file_url?: string | null;
};
export type ReadPoliceDepartment = {
  /** Police department name, if explicitly mentioned, otherwise null */
  name?: string | null;
  /** Police department state, if explicitly mentioned, otherwise null. e.g. 'California' */
  state_name?: string | null;
  /** Police department state code, if explicitly mentioned, otherwise null. e.g. 'CA' */
  state_code?: string | null;
  /** The best available contact methods for the police department for requesting records, documents or reports.
    - NON-EMERGENCY phone lines
    - Any specific 'records' department phone or fax numbers
    - Mailing addresses for records requests
    - Email addresses for records requests
    - Online portals for records requests
    - Department homepage */
  handles?: ContactHandleSchema[] | null;
  /** Primary source provided aliases or abbreviations for the department, e.g. 'SFPD' */
  aliases?: string[] | null;
  /** Ascertained jurisdiction type of the police department / agency, if explicitly
    stated. */
  jurisdiction_type?: JurisdictionType | null;
  /** Primary source provided name or description of the jurisdiction,
    e.g. 'San Francisco', 'Coffee County, Tennessee', 'Major Highways of Tennessee' */
  jurisdiction_name?: string | null;
  /** system use: Whether the police department has been enriched with additional information */
  enrichment_status?: PoliceDepartmentEnrichmentStatus | null;
  /** A concise, but comprehensive description of the police department, if explicitly mentioned, otherwise null.
    e.g. 'The San Francisco Police Department is the primary law enforcement agency for the City and County of San Francisco.' */
  description?: string | null;
  records_channels?: ReadDepartmentRecordsChannel[];
  /** The time zone of the police department, if explicitly mentioned, otherwise null.
    e.g. 'America/Los_Angeles' */
  time_zone?: TimeZone | null;
  /** Flag to mark department as do not call - prevents voice agent outreach */
  do_not_call?: boolean;
  id: string | string;
  created_at: string;
  modified_at: string;
};
export type ReadPoliceReport = {
  /** Guide to attestation statuses:
    
    <inferred>
    The involvement of this department is a system generated guess based on some circumstantial clues, like the location of the incident,
    the department hasn't been directly named anywhere in the case context. Always use this when the department is not
    directly named in the case context.
    </inferred>
    <unclear>
    The department has been named somewhere in the case context, but we have mixed indications as to whether it was involved in the incident.
    </unclear>
    <attested_unverified>
    Secondary source (client intake, staff note) attestation
    that a police report exists with this department.
    </attested_unverified>
    <rejected_verified>
    Primary source (department representative) confirmation that
    a police report does not exist within the department, e.g. 'We do not have a
    report for this incident'
    </rejected_verified>
    <attested_verified>
    Primary source (department representative, or official
    communication) confirmation that the department produced or is producing a report,
    e.g. an officer communication that says 'We are producing a report'
    </attested_verified>
    <verified_received>
    Ulimate confirmation: we have the final police report in hand
    can only be inferred from the matching incident report itself
    </verified_received> */
  attestation_status?: PoliceReportAttestationStatus | null;
  /** In at most 3 concise sentences, summarize our investigation into this department's involvement,
    critically addressing all three of the following points:
    
    1) How we discovered this department (source & reasoning)
    2) What actions, if any, we've taken to verify involvement
    3) Current understanding and next steps.
    
    Derivative examples:
    INFERRED status:
    "Guessed via location analysis - incident at I-405 & Sunset suggests CHP jurisdiction.
    No additional information yet. Next: call to confirm."
    
    UNCLEAR status:
    "Client mentioned 'LAPD responded' in intake (10/15). Called records dept 3x (10/20, 10/22, 10/25) - conflicting info.
    Officer Smith said 'checking their system and to call back in a few days'.
    Next: Request supervisor callback for definitive answer."
    
    ATTESTED_UNVERIFIED status:
    "Client's intake form states 'SFPD took report at scene, got card from Officer Chen #4521'.
    Found matching Officer Chen in SFPD roster. Called records (11/1) - hold times excessive.
    Next: Submit online records request with officer badge number."
    
    REJECTED_VERIFIED status:
    "Initially suspected due to jurisdiction overlap. Spoke with Records Supervisor Johnson (11/5, recorded).
    Confirmed: 'We did not respond to that location on [date]. Try County Sheriff.'
    Investigation complete for this department. Consider marking inactive."
    
    ATTESTED_VERIFIED status:
    "Department confirmed involvement via email from Records Dept (11/10): 'Report #2024-1234 is being processed.'
    Processing fee paid ($25). Estimated delivery: 7-10 business days.
    Next: Follow up on 11/20 if not received."
    
    VERIFIED_RECEIVED status:
    "Full report was found in case files. 8 pages, includes witness statements and diagram.
    Officer Rodriguez #7823 was primary. Report confirms client's account of events.
    Investigation complete. All documentation uploaded to case file." */
  attestation_description?: string | null;
  /** Attested availability status of the police report, only when explicitly mentioned by a primary source, otherwise null.
    <verified_ready>
    The police department has confirmed that the report is available.
    </verified_ready>
    <verified_pending>
    The police department has confirmed they are responsible for the report, but that the report is not available yet, but will be on some timeline.
    </verified_pending>
    <unknown>
    No explicit mention yet of whether the police department is responsible for the report, or if we have any other information to go on.
    </unknown> */
  availability_status?: PoliceReportAvailabilityStatus | null;
  /** When availability status is pending, an estimated date for when the police report might be ready, only when explicitly mentioned by a primary source, otherwise null.
    e.g. 'The report is expected to be ready by 11/20' */
  availability_estimate?: string | null;
  /** A concise, but comprehensive description of the police involvement, if explicitly mentioned, otherwise null.
    e.g. 'The police department responded to the incident, took statements from witnesses, spoke to both drivers, etc.' */
  description?: string | null;
  /** Department-issued identifier for the report
    e.g. police report number, TCR number, accident report number, etc. */
  report_identifier?: string | null;
  /** The date of the incident noted on the report, if explicitly defined by a primary source,
    otherwise null. */
  reported_incident_date?: string | null;
  /** Location of the incident, if explicitly mentioned, otherwise null.
    e.g. '123 Main St, San Francisco, CA 94133' */
  reported_location?: string | null;
  /** Officers explicitly mentioned in the text, if explicitly mentioned, otherwise null
    e.g. 'Officer John Doe, Officer Jane Smith, etc.' */
  involved_officers?: OfficerDetail[] | null;
  /** Internal ID of the associated police department - not set during extraction */
  police_department_id?: string | string | null;
  /** [system use] The reason this report has been escalated to a human. */
  escalation_reason?: PoliceReportEscalationReason | null;
  matter_id: string | string;
  id: string | string;
  created_at: string;
  modified_at: string;
  police_department: ReadPoliceDepartment;
};
export type TreatmentRecordRequestActor = 'user' | 'agent';
export type TreatmentRecordTypes =
  | 'Bills'
  | 'Records'
  | 'Radiology'
  | 'Other'
  | 'Unknown';
export type TreatmentRecordRequestStatus =
  | 'not_ready'
  | 'pending'
  | 'request_submitted'
  | 'request_received'
  | 'awaiting_invoice'
  | 'pending_payment'
  | 'records_pending'
  | 'records_delivered'
  | 'records_ingested'
  | 'rejected'
  | 'cancelled';
export type Blockers =
  | 'HIPAA_PACKAGE_NOT_READY'
  | 'CLIENT_INFORMATION_NOT_ENTERED'
  | 'PROVIDER_CONTACT_INFORMATION_NOT_ENTERED'
  | 'PORTAL_REQUEST_ID_REQUIRED'
  | 'NO_REQUEST_SENT_AT'
  | 'VOICE_AI_INFORMATION_NOT_ENTERED';
export type TreatmentRecordRequestAutomationBlockedSchema = {
  details?: string | null;
  checked_status_at?: string | null;
  status?: 'blocked';
  blocked_by?: Blockers[];
};
export type TreatmentRecordRequestAutomationFailedSchema = {
  details?: string | null;
  checked_status_at?: string | null;
  status?: 'failed';
};
export type TreatmentRecordRequestAutomationIdleSchema = {
  details?: string | null;
  checked_status_at?: string | null;
  status?: 'idle';
};
export type TreatmentRecordRequestAutomationRunningSchema = {
  details?: string | null;
  checked_status_at?: string | null;
  status?: 'running';
};
export type TreatmentRecordRequestAutomationSleepingSchema = {
  details?: string | null;
  checked_status_at?: string | null;
  status?: 'sleeping';
  sleep_until: string;
};
export type PatientInformation = {
  client_name: string;
  client_date_of_birth: string;
};
export type TreatmentRecordRequestChannelType =
  | 'portal'
  | 'human'
  | 'email'
  | 'fax'
  | 'mail'
  | 'phone';
export type RequestDetails = {
  request_method: TreatmentRecordRequestChannelType;
  request_sent_at: string;
  request_method_target: string;
  contact_number: string;
};
export type RequesteeInformation = {
  requestee_name: string;
};
export type VoiceAiCallArgs = {
  patient_information: PatientInformation;
  request_details: RequestDetails;
  requestee_information: RequesteeInformation;
  /** Human-readable description of the requested records (e.g., Medical Records, Bills, Radiology). */
  request_type_label?: string | null;
  /** Skip business-hours scheduling and execute the call immediately. Used for manual/bulk calls that should run ASAP. */
  skip_scheduling_delay?: boolean;
};
export type AlternateContactHandleType = 'fax' | 'mailing_address';
export type AlternateContactSchema = {
  /** Type of contact method (fax or mailing address) */
  handle_type: AlternateContactHandleType;
  /** The actual contact value (fax number or mailing address) */
  value: string;
  /** Optional human-readable label or description */
  label?: string | null;
  /** Optional confidence score from AI extraction (0-1) */
  confidence?: number | null;
};
export type ContactDiscoveryCallSummarySchema = {
  /** Short text describing what the facility told us. */
  call_summary: string;
  /** List of submission channels (portal, fax, email) mentioned during the call. */
  submission_methods?: string[];
  /** Documents or prerequisites (HIPAA, ID, invoices) called out by the facility. */
  required_documents?: string[];
  /** Structured alternates (fax numbers, mailing addresses) discovered during the call. */
  alternate_contacts?: AlternateContactSchema[];
  /** Optional free-form notes for additional context. */
  notes?: string | null;
};
export type TreatmentRecordRequestMetadataSchema = {
  slack_message_id?: string | null;
  disable_automations?: boolean;
  automation_state_v2?:
    | (
        | ({
            status: 'blocked';
          } & TreatmentRecordRequestAutomationBlockedSchema)
        | ({
            status: 'failed';
          } & TreatmentRecordRequestAutomationFailedSchema)
        | ({
            status: 'idle';
          } & TreatmentRecordRequestAutomationIdleSchema)
        | ({
            status: 'running';
          } & TreatmentRecordRequestAutomationRunningSchema)
        | ({
            status: 'sleeping';
          } & TreatmentRecordRequestAutomationSleepingSchema)
      )
    | null;
  ad_hoc_voice_ai_args?: VoiceAiCallArgs | null;
  front_conversation_id?: string | null;
  front_conversation_url?: string | null;
  /** Number of days between voice AI follow-up calls */
  voice_ai_check_interval_days?: number;
  next_scheduled_call?: string | null;
  request_submitted_at?: string | null;
  initial_contact_method_call_completed_at?: string | null;
  voice_ai_call_summary?: string | null;
  voice_ai_discovered_alternates?: {
    [key: string]: any;
  }[];
  voice_ai_call_id?: string | null;
  superseded_by_request_id?: string | null;
  supersession_reason?: string | null;
  last_notification_category?: string | null;
  last_notification_subcategory?: string | null;
  last_call_key_information?: {
    [key: string]: any;
  };
  last_voice_ai_call_at?: string | null;
  last_voice_ai_call_summary?: string | null;
  dynamic_retry_delay_hours?: number | null;
  contact_discovery_summary?: ContactDiscoveryCallSummarySchema | null;
  contact_discovery_last_updated_at?: string | null;
  hipaa_package_locked?: boolean;
  hipaa_package_locked_at?: string | null;
  hipaa_package_submission_channel?: TreatmentRecordRequestChannelType | null;
  hipaa_package_submission_workflow_id?: string | null;
  alternate_contact?: AlternateContactSchema | null;
  forked_from_request_id?: string | null;
};
export type TreatmentRecordRequestPortalType =
  | 'smart_request'
  | 'mro'
  | 'chartswap';
export type PortalRecordType = {
  id: string | string;
  created_at: string;
  modified_at: string;
  original_name?: string | null;
  record_type: TreatmentRecordTypes;
};
export type PortalProvider = {
  id: string | string;
  created_at: string;
  modified_at: string;
  name: string;
  portal: TreatmentRecordRequestPortalType;
  active?: boolean;
  external_id?: string | null;
  site?: string | null;
  website?: string | null;
  phone?: string | null;
  email?: string | null;
  fax?: string | null;
  address_line_1?: string | null;
  address_line_2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  record_types?: PortalRecordType[] | null;
};
export type TreatmentRecordRequestChannelSchema = {
  id: string | string;
  created_at: string;
  modified_at: string;
  type: TreatmentRecordRequestChannelType;
  portal_provider_id?: string | string | null;
  phone_number?: string | null;
  address?: string | null;
  email?: string | null;
  fax?: string | null;
  instructions?: string | null;
  portal_record_request_id?: string | null;
  provider_contact_unit_handle_id?: string | string | null;
  portal_provider?: PortalProvider | null;
};
export type FaxTransmissionStatusSchema = {
  id?: string | string | null;
  status?: ('pending' | 'sent' | 'delivered' | 'failed') | null;
  sent_at?: string | null;
  delivered_at?: string | null;
  failed_at?: string | null;
  transmission_id?: string | null;
  error_message?: string | null;
};
export type TreatmentRecordRequestSchema = {
  id: string | string;
  created_at: string;
  modified_at: string;
  provider_id: string | string;
  actor?: TreatmentRecordRequestActor;
  is_certified?: boolean;
  is_urgent?: boolean;
  provided_records?: TreatmentRecordTypes[];
  status?: TreatmentRecordRequestStatus;
  metadata?: TreatmentRecordRequestMetadataSchema;
  channel?: TreatmentRecordRequestChannelSchema | null;
  fax_transmission_status?: FaxTransmissionStatusSchema | null;
  task_id?: string | string | null;
};
export type DetailedTaskSchema = {
  task: TaskWithUpdatesSchema;
  firm: FirmSchema;
  matter: ReadMatterSchema;
  client: MatterClientSchema;
  coverage?: CoverageSchema | null;
  provider?: MedicalProviderSchema | null;
  assignee?: CustomUserSchema | null;
  police_report?: ReadPoliceReport | null;
  treatment_record_request?: TreatmentRecordRequestSchema | null;
};
export type DeliverableAgentContext = {
  /** Single sentence (≤20 words) explaining why this deliverable was created. */
  rationale?: string | null;
};
export type DeliverableMatchStatus = 'Needs Review' | 'Approved' | 'Rejected';
export type DeliverableMatchContext = {
  /** ONE concise sentence explaining the match. Example: 'Document contains signed retainer dated 4/15/2024.' */
  rationale?: string | null;
  /** Up to 3 short, direct quotes supporting the match. Keep each quote under 20 words. */
  key_evidence?: string[] | null;
};
export type DeliverableMatchSchema = {
  status?: DeliverableMatchStatus | null;
  confidence?: number | null;
  context?: DeliverableMatchContext | null;
  id: string | string;
  deliverable_id: string | string;
  artifact?: DetailedArtifactSchema | null;
  front_conversation_id?: string | string | null;
  created_at: string;
  modified_at: string;
  user_id?: string | string | null;
  user_name?: string | null;
  agent?: AgentType | null;
};
export type DeliverableMatchSchemaRead = {
  status?: DeliverableMatchStatus | null;
  confidence?: number | null;
  context?: DeliverableMatchContext | null;
  id: string | string;
  deliverable_id: string | string;
  artifact?: DetailedArtifactSchema | null;
  front_conversation_id?: string | string | null;
  created_at: string;
  modified_at: string;
  user_id?: string | string | null;
  user_name?: string | null;
  agent?: AgentType | null;
  /** Title of the artifact if match is to an artifact. */
  artifact_id: string | string | null;
};
export type DeliverableSchema = {
  name: string;
  prompt: string;
  artifact_criteria?: DeliverableArtifactCriteria | null;
  anchors?: DeliverableAnchors | null;
  status?: DeliverableStatus | null;
  id: string | string;
  task: DetailedTaskSchema;
  created_at: string;
  modified_at: string;
  user_id?: string | string | null;
  agent?: AgentType | null;
  agent_context?: DeliverableAgentContext | null;
  matches?: DeliverableMatchSchema[];
  /** Canonical group of the task (backwards compatibility). */
  task_canonical_group: TaskGroups;
  /** Canonical type of the task (backwards compatibility). */
  task_canonical_type: TaskType;
};
export type DeliverableSchemaRead = {
  name: string;
  prompt: string;
  artifact_criteria?: DeliverableArtifactCriteria | null;
  anchors?: DeliverableAnchors | null;
  status?: DeliverableStatus | null;
  id: string | string;
  task: DetailedTaskSchema;
  created_at: string;
  modified_at: string;
  user_id?: string | string | null;
  agent?: AgentType | null;
  agent_context?: DeliverableAgentContext | null;
  matches?: DeliverableMatchSchemaRead[];
  /** ID of the task (backwards compatibility). */
  task_id: string | string;
  /** Name of the task (backwards compatibility). */
  task_name: string;
  /** Canonical group of the task (backwards compatibility). */
  task_canonical_group: TaskGroups;
  /** Canonical type of the task (backwards compatibility). */
  task_canonical_type: TaskType;
  /** Number of matches needing review. */
  pending_matches: number;
  /** Number of rejected matches. */
  rejected_matches: number;
  /** Whether completing the parent task requires evidence for this deliverable. */
  requires_evidence?: boolean;
};
export type DeliverableCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  deliverable: DeliverableSchema;
  task_id: string | string;
  name: string;
  prompt: string;
};
export type DeliverableCreatedEventRead = {
  /** PK of the row that changed */
  target_id: string | string;
  deliverable: DeliverableSchemaRead;
  task_id: string | string;
  name: string;
  prompt: string;
};
export type ReadDeliverableCreatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'deliverable_created';
  version: number;
  target_type: 'app.deliverable';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: DeliverableCreatedEvent;
};
export type ReadDeliverableCreatedEventRead = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'deliverable_created';
  version: number;
  target_type: 'app.deliverable';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: DeliverableCreatedEventRead;
};
export type DeliverableMatchApprovedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  deliverable_match: DeliverableMatchSchema;
  deliverable_id: string | string;
  artifact_id: string | string;
};
export type DeliverableMatchApprovedEventRead = {
  /** PK of the row that changed */
  target_id: string | string;
  deliverable_match: DeliverableMatchSchemaRead;
  deliverable_id: string | string;
  artifact_id: string | string;
};
export type ReadDeliverableMatchApprovedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'deliverable_match_approved';
  version: number;
  target_type: 'app.deliverablematch';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: DeliverableMatchApprovedEvent;
};
export type ReadDeliverableMatchApprovedEventRead = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'deliverable_match_approved';
  version: number;
  target_type: 'app.deliverablematch';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: DeliverableMatchApprovedEventRead;
};
export type DeliverableMatchCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  deliverable_match: DeliverableMatchSchema;
  deliverable: DeliverableSchema;
  artifact_id: string | string;
  confidence: number | null;
};
export type DeliverableMatchCreatedEventRead = {
  /** PK of the row that changed */
  target_id: string | string;
  deliverable_match: DeliverableMatchSchemaRead;
  deliverable: DeliverableSchemaRead;
  artifact_id: string | string;
  confidence: number | null;
};
export type ReadDeliverableMatchCreatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'deliverable_match_created';
  version: number;
  target_type: 'app.deliverablematch';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: DeliverableMatchCreatedEvent;
};
export type ReadDeliverableMatchCreatedEventRead = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'deliverable_match_created';
  version: number;
  target_type: 'app.deliverablematch';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: DeliverableMatchCreatedEventRead;
};
export type DeliverableMatchStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  deliverable_match: DeliverableMatchSchema;
  deliverable_id: string | string;
  artifact_id: string | string;
  old_status: DeliverableMatchStatus | null;
  new_status: DeliverableMatchStatus;
};
export type DeliverableMatchStatusChangedEventRead = {
  /** PK of the row that changed */
  target_id: string | string;
  deliverable_match: DeliverableMatchSchemaRead;
  deliverable_id: string | string;
  artifact_id: string | string;
  old_status: DeliverableMatchStatus | null;
  new_status: DeliverableMatchStatus;
};
export type ReadDeliverableMatchStatusChangedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'deliverable_match_status_changed';
  version: number;
  target_type: 'app.deliverablematch';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: DeliverableMatchStatusChangedEvent;
};
export type ReadDeliverableMatchStatusChangedEventRead = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'deliverable_match_status_changed';
  version: number;
  target_type: 'app.deliverablematch';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: DeliverableMatchStatusChangedEventRead;
};
export type DeliverableSatisfiedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  deliverable: DeliverableSchema;
  task_id: string | string;
  satisfied_by_match_id: string | string;
};
export type DeliverableSatisfiedEventRead = {
  /** PK of the row that changed */
  target_id: string | string;
  deliverable: DeliverableSchemaRead;
  task_id: string | string;
  satisfied_by_match_id: string | string;
};
export type ReadDeliverableSatisfiedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'deliverable_satisfied';
  version: number;
  target_type: 'app.deliverable';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: DeliverableSatisfiedEvent;
};
export type ReadDeliverableSatisfiedEventRead = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'deliverable_satisfied';
  version: number;
  target_type: 'app.deliverable';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: DeliverableSatisfiedEventRead;
};
export type DeliverableStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  deliverable: DeliverableSchema;
  old_status: DeliverableStatus | null;
  new_status: DeliverableStatus;
};
export type DeliverableStatusChangedEventRead = {
  /** PK of the row that changed */
  target_id: string | string;
  deliverable: DeliverableSchemaRead;
  old_status: DeliverableStatus | null;
  new_status: DeliverableStatus;
};
export type ReadDeliverableStatusChangedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'deliverable_status_changed';
  version: number;
  target_type: 'app.deliverable';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: DeliverableStatusChangedEvent;
};
export type ReadDeliverableStatusChangedEventRead = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'deliverable_status_changed';
  version: number;
  target_type: 'app.deliverable';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: DeliverableStatusChangedEventRead;
};
export type DocumentTemplateSchema = {
  id: string | string;
  name: string;
  template_type: string;
  requires_signatures: boolean;
  client_signature_required?: boolean;
  attorney_signature_required?: boolean;
  attorney_id?: string | string | null;
};
export type DocumentInstanceSchema = {
  id: string | string;
  status: string;
  created_at?: string | null;
  document_template: DocumentTemplateSchema;
  cached_pdf_url?: string | null;
  field_values_source?: string | null;
};
export type DocumentCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  document_id?: string | string | null;
  document: DocumentInstanceSchema;
};
export type ReadDocumentCreatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'document_created';
  version: number;
  target_type: 'app.documentinstance';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: DocumentCreatedEvent;
};
export type DocumentDeletedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  document_id?: string | string | null;
  document: DocumentInstanceSchema;
};
export type ReadDocumentDeletedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'document_deleted';
  version: number;
  target_type: 'app.documentinstance';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: DocumentDeletedEvent;
};
export type HipaaGenerationAuditEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  document_id: string | string;
  template_id: string | string;
  request_id?: string | string | null;
  defaults: {
    [key: string]: any;
  }[];
  submitted_merge_fields: {
    [key: string]: any;
  };
  diff: {
    [key: string]: any;
  };
};
export type ReadHipaaGenerationAuditEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'document_hipaa_package_generation_audit';
  version: number;
  target_type: 'app.documentinstance';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: HipaaGenerationAuditEvent;
};
export type LoRGenerationAuditEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  document_id: string | string;
  template_id: string | string;
  coverage_id?: string | string | null;
  defaults: {
    [key: string]: any;
  }[];
  submitted_merge_fields: {
    [key: string]: any;
  };
  diff: {
    [key: string]: any;
  };
};
export type ReadLoRGenerationAuditEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'document_lor_generation_audit';
  version: number;
  target_type: 'app.documentinstance';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: LoRGenerationAuditEvent;
};
export type DocumentInstanceStatus =
  | 'CREATED'
  | 'SENT'
  | 'COMPLETED'
  | 'VOIDED'
  | 'SIGNED';
export type DocumentStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  document_id?: string | string | null;
  document: DocumentInstanceSchema;
  old_status?: DocumentInstanceStatus | null;
  new_status?: DocumentInstanceStatus | null;
};
export type ReadDocumentStatusChangedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'document_status_changed';
  version: number;
  target_type: 'app.documentinstance';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: DocumentStatusChangedEvent;
};
export type ExpenseCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  expense: ReadExpense;
};
export type ReadExpenseCreatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'expense_created';
  version: number;
  target_type: 'app.expense';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: ExpenseCreatedEvent;
};
export type ExpenseDeletedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  expense: ReadExpense;
};
export type ReadExpenseDeletedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'expense_deleted';
  version: number;
  target_type: 'app.expense';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: ExpenseDeletedEvent;
};
export type ExpenseUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  expense: ReadExpense;
  diff: {
    [key: string]: any;
  };
  previous?: ReadExpense | null;
};
export type ReadExpenseUpdatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'expense_updated';
  version: number;
  target_type: 'app.expense';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: ExpenseUpdatedEvent;
};
export type RuleEvaluationStatus = 'MATCHED' | 'NO_MATCH';
export type TriggerTaskType = 'EXTERNAL' | 'INTERNAL';
export type RuleEvaluationSchema = {
  id: string | string;
  rule_version_id: string | string;
  internal_task_id?: string | string | null;
  external_task_id?: string | string | null;
  workflow_id?: string | null;
  evaluation_status: RuleEvaluationStatus;
  links_created: number;
  evaluated_at: string;
  created_at: string;
  /** Backward compatibility: derive task type from FK fields. */
  trigger_task_type: TriggerTaskType;
};
export type RuleEvaluationSchemaRead = {
  id: string | string;
  rule_version_id: string | string;
  internal_task_id?: string | string | null;
  external_task_id?: string | string | null;
  workflow_id?: string | null;
  evaluation_status: RuleEvaluationStatus;
  links_created: number;
  evaluated_at: string;
  created_at: string;
  /** Backward compatibility: derive task type from FK fields. */
  trigger_task_type: TriggerTaskType;
  /** Backward compatibility: derive task ID from FK fields. */
  trigger_task_id: string | string | null;
};
export type ExternalTaskRuleEvaluationCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  rule_evaluation: RuleEvaluationSchema;
  rule_id: string | string;
  rule_name: string;
};
export type ExternalTaskRuleEvaluationCreatedEventRead = {
  /** PK of the row that changed */
  target_id: string | string;
  rule_evaluation: RuleEvaluationSchemaRead;
  rule_id: string | string;
  rule_name: string;
};
export type ReadExternalTaskRuleEvaluationCreatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'external_task_rule_evaluation_created';
  version: number;
  target_type: 'app.ruleevaluation';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: ExternalTaskRuleEvaluationCreatedEvent;
};
export type ReadExternalTaskRuleEvaluationCreatedEventRead = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'external_task_rule_evaluation_created';
  version: number;
  target_type: 'app.ruleevaluation';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: ExternalTaskRuleEvaluationCreatedEventRead;
};
export type HipaaAuthorizationSignatureUploadedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  authorization_id?: string | string | null;
  client_id: string | string;
  signature_date: string;
  file_name?: string | null;
  file_size?: number | null;
};
export type ReadHipaaAuthorizationSignatureUploadedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'hipaa_authorization_signature_uploaded';
  version: number;
  target_type: 'app.hipaaauthorizationsignature';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: HipaaAuthorizationSignatureUploadedEvent;
};
export type IncidentMentionStubSchema = {
  id: string | string;
  created_at: string;
  artifact_id: string | string;
  artifact_type: ArtifactType;
  citation_ids?: string[] | null;
  confidence?: number | null;
  context?: string | null;
  reasoning?: string | null;
};
export type LocationNormalizationStatus =
  | 'pending'
  | 'success'
  | 'failed'
  | 'low_confidence';
export type IncidentLocationSchema = {
  /** Original location from extraction */
  raw_location?: string | null;
  /** Google Maps Place ID */
  place_id?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  formatted_address?: string | null;
  short_formatted_address?: string | null;
  display_name?: string | null;
  street_number?: string | null;
  route?: string | null;
  city?: string | null;
  county?: string | null;
  state?: string | null;
  state_long?: string | null;
  postal_code?: string | null;
  country?: string | null;
  country_long?: string | null;
  plus_code_global?: string | null;
  plus_code_compound?: string | null;
  primary_type?: string | null;
  types?: string[];
  google_maps_uri?: string | null;
  normalization_confidence?: number | null;
  confidence_reasoning?: string | null;
  normalization_status?: LocationNormalizationStatus | null;
  satellite_image_url?: string | null;
  /** Street View facing north (0°) */
  streetview_image_url_0?: string | null;
  /** Street View facing east (90°) */
  streetview_image_url_90?: string | null;
  /** Street View facing south (180°) */
  streetview_image_url_180?: string | null;
  /** Street View facing west (270°) */
  streetview_image_url_270?: string | null;
};
export type IncidentSchema = {
  /** Concise, 1-2 sentence summary of the facts of the incident, limited to events that occurred on the date of loss. Involved parties, location, and nature of the incident should be included. */
  description?: string | null;
  date_of_loss?: string | null;
  /** Time of day when the incident occurred, helping establish circumstances and potential contributing factors. */
  time_of_loss?: string | null;
  /** Where the incident took place, formatted for geocoding.
    
    Police reports typically include location in standardized formats - preserve these exactly:
    - Street addresses: "1234 OAK ST" or "1234 Oak Street"
    - Intersections: "MAIN ST / 5TH AVE" or "Main St at 5th Ave"
    - Highway locations: "I-95 NB MM 42.3" or "I-95 Northbound mile marker 42"
    - Relative positions: "100 FT N OF MAIN ST" or "100 feet north of Main St"
    - Reference points: "IFO 123 MAIN ST" (in front of) or "in front of 123 Main St"
    
    When extracting from police reports, look for fields like:
    - "Location of Occurrence", "Crash Location", "Scene Location"
    - "Street/Highway", "At Intersection Of", "Nearest Cross Street"
    - "Feet/Direction From", "Reference Point"
    
    Output format: "[Address/Intersection], [City], [State] [ZIP if available]"
    
    Examples:
    - "1234 Oak Street, Springfield, IL 62701"
    - "Main Street and 5th Avenue, Newark, NJ"
    - "I-95 Northbound mile marker 42, Philadelphia, PA"
    - "Route 1, 100 feet south of Oak Avenue, Edison, NJ"
    - "123 Main St (in front of Walmart), Anytown, NJ"
    
    Include city and state when available. Do not include narrative text. */
  location?: string | null;
  /** Latitude coordinate if explicitly provided in the source (e.g., from a police report or GPS data). Only extract if exact coordinates are stated, do not estimate. */
  extracted_latitude?: number | null;
  /** Longitude coordinate if explicitly provided in the source (e.g., from a police report or GPS data). Only extract if exact coordinates are stated, do not estimate. */
  extracted_longitude?: number | null;
  /** Cross street at incident location, if explicitly stated, otherwise null. */
  cross_street?: string | null;
  /** Environmental conditions at the time of incident that may have contributed to or affected the circumstances. */
  weather_conditions?: string | null;
  /** Factual description of injuries sustained, important for damages assessment and medical records analysis. */
  injuries_described?: string | null;
  /** Category or nature of the incident (vehicle collision, slip and fall, workplace injury, etc.) for case classification and strategy. */
  incident_type?: string | null;
  /** Whether emergency medical care was sought immediately following the incident - relevant for damages and treatment documentation. */
  hospital?: string | null;
  /** Whether emergency medical services responded to the scene - indicates severity and creates potential documentation trail. */
  ambulance?: string | null;
  /** Whether law enforcement documented the incident - critical for obtaining official reports and witness information. */
  police_report?: string | null;
  /** Ongoing medical care status - important for understanding continuing damages and treatment coordination. */
  current_treatment?: string | null;
  /** Presence of witnesses to the incident - essential for case investigation and potential testimony. */
  witnesses?: string | null;
  /** Whether witness statements were obtained - important for preserving testimony and case preparation. */
  statements?: string | null;
  /** Whether photographic or video evidence was captured - crucial for case documentation and demonstrative evidence. */
  photos?: string | null;
  /** Any prior legal representation for this incident - important for conflicts analysis and case coordination. */
  representation?: string | null;
  id: string | string;
  matter_id: string | string;
  created_at: string;
  modified_at: string;
  /**
            List of mentions of this incident in the matter, if any.
             */
  mentions?: IncidentMentionStubSchema[] | null;
  /** Normalized location data from Google Maps */
  normalized_location?: IncidentLocationSchema | null;
};
export type IncidentCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  incident: IncidentSchema;
};
export type ReadIncidentCreatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'incident_created';
  version: number;
  target_type: 'app.incidentnote';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: IncidentCreatedEvent;
};
export type IncidentUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  incident: IncidentSchema;
  diff: {
    [key: string]: any;
  };
  user_id?: string | string | null;
};
export type ReadIncidentUpdatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'incident_updated';
  version: number;
  target_type: 'app.incidentnote';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: IncidentUpdatedEvent;
};
export type LienholderType = 'PROVIDER' | 'COVERAGE';
export type LienType = 'STATUTORY' | 'CONTRACTUAL';
export type LienStatus = 'ACTIVE' | 'RESOLVED';
export type ReadLien = {
  id: string | string;
  matter_id: string | string;
  lienholder_type: LienholderType;
  lienholder_coverage?: CoverageSummary | null;
  lienholder_provider?: ProviderSummary | null;
  lien_type: LienType;
  status: LienStatus;
  exposure_amount: string;
  created_at: string;
  modified_at: string;
};
export type LienCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  lien: ReadLien;
};
export type ReadLienCreatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'lien_created';
  version: number;
  target_type: 'app.lien';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: LienCreatedEvent;
};
export type LienDeletedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  lien: ReadLien;
};
export type ReadLienDeletedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'lien_deleted';
  version: number;
  target_type: 'app.lien';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: LienDeletedEvent;
};
export type LienUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  lien: ReadLien;
  diff: {
    [key: string]: any;
  };
  previous?: ReadLien | null;
};
export type ReadLienUpdatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'lien_updated';
  version: number;
  target_type: 'app.lien';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: LienUpdatedEvent;
};
export type LoaOutreachStatus =
  | 'running'
  | 'paused'
  | 'completed_loa_received'
  | 'completed_rejected'
  | 'stopped_manually'
  | 'monthly_cadence';
export type LoaOutreachStepType =
  | 'auto_email'
  | 'auto_fax'
  | 'manual_call'
  | 'manual_email'
  | 'supervisor_escalation';
export type LoaOutreachCampaignSchema = {
  id: string | string;
  matter_id: string | string;
  coverage_id: string | string;
  workflow_id: string;
  status: LoaOutreachStatus;
  current_step_index: number;
  current_step_label: string;
  next_action_date?: string | null;
  next_action_type?: LoaOutreachStepType | null;
  next_action_label?: string;
  last_contact_date?: string | null;
  last_contact_type?: LoaOutreachStepType | null;
  total_contact_count?: number;
  email_thread_id?: string;
  stopped_reason?: string | null;
  created_at: string;
  modified_at: string;
};
export type LoaChaserCampaignCompletedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  campaign: LoaOutreachCampaignSchema;
  outcome: LoaOutreachStatus;
};
export type ReadLoaChaserCampaignCompletedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'loa_chaser_campaign_completed';
  version: number;
  target_type: 'app.loaoutreachcampaign';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: LoaChaserCampaignCompletedEvent;
};
export type LoaChaserCampaignPausedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  campaign: LoaOutreachCampaignSchema;
};
export type ReadLoaChaserCampaignPausedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'loa_chaser_campaign_paused';
  version: number;
  target_type: 'app.loaoutreachcampaign';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: LoaChaserCampaignPausedEvent;
};
export type LoaChaserCampaignResumedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  campaign: LoaOutreachCampaignSchema;
};
export type ReadLoaChaserCampaignResumedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'loa_chaser_campaign_resumed';
  version: number;
  target_type: 'app.loaoutreachcampaign';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: LoaChaserCampaignResumedEvent;
};
export type LoaChaserCampaignStartedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  campaign: LoaOutreachCampaignSchema;
  starting_step: string;
  email_thread_id: string;
};
export type ReadLoaChaserCampaignStartedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'loa_chaser_campaign_started';
  version: number;
  target_type: 'app.loaoutreachcampaign';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: LoaChaserCampaignStartedEvent;
};
export type LoaChaserCampaignStoppedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  campaign: LoaOutreachCampaignSchema;
  reason?: string | null;
};
export type ReadLoaChaserCampaignStoppedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'loa_chaser_campaign_stopped';
  version: number;
  target_type: 'app.loaoutreachcampaign';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: LoaChaserCampaignStoppedEvent;
};
export type LoaChaserManualActivityLoggedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  campaign: LoaOutreachCampaignSchema;
  activity_type: LoaOutreachStepType;
  notes: string;
  executed_at: string;
};
export type ReadLoaChaserManualActivityLoggedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'loa_chaser_manual_activity_logged';
  version: number;
  target_type: 'app.loaoutreachcampaign';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: LoaChaserManualActivityLoggedEvent;
};
export type LoaChaserScheduleRealignedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  campaign: LoaOutreachCampaignSchema;
  previous_next_step: string;
  previous_due_date: string;
  new_next_step: string;
  new_due_date: string;
  trigger: string;
};
export type ReadLoaChaserScheduleRealignedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'loa_chaser_schedule_realigned';
  version: number;
  target_type: 'app.loaoutreachcampaign';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: LoaChaserScheduleRealignedEvent;
};
export type LoaChaserSnoozedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  campaign: LoaOutreachCampaignSchema;
  snooze_days: number;
  previous_due_date: string;
  new_due_date: string;
};
export type ReadLoaChaserSnoozedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'loa_chaser_snoozed';
  version: number;
  target_type: 'app.loaoutreachcampaign';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: LoaChaserSnoozedEvent;
};
export type LoaChaserStepChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  campaign: LoaOutreachCampaignSchema;
  previous_step_id: string;
  previous_step_label: string;
  new_step_id: string;
  new_step_label: string;
  /** 'skip' (forward) or 'reset' (backward) */
  direction: string;
};
export type ReadLoaChaserStepChangedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'loa_chaser_step_changed';
  version: number;
  target_type: 'app.loaoutreachcampaign';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: LoaChaserStepChangedEvent;
};
export type LoaChaserStepExecutedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  campaign: LoaOutreachCampaignSchema;
  step_id: string;
  step_label: string;
  step_type: LoaOutreachStepType;
  was_auto?: boolean;
};
export type ReadLoaChaserStepExecutedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'loa_chaser_step_executed';
  version: number;
  target_type: 'app.loaoutreachcampaign';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: LoaChaserStepExecutedEvent;
};
export type LoRDataUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  document_id?: string | string | null;
  coverage_id?: string | string | null;
  mapping?: string | null;
  old_value?: string | null;
  new_value?: string | null;
};
export type ReadLoRDataUpdatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'lor_data_updated';
  version: number;
  target_type: 'app.coverage';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: LoRDataUpdatedEvent;
};
export type MatterClientCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  matter_client: MatterClientSchema;
};
export type ReadMatterClientCreatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'matter_client_created';
  version: number;
  target_type: 'app.matterclient';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: MatterClientCreatedEvent;
};
export type MatterClientPhoneNumberAddedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  matter_client: MatterClientSchema;
  phone_number: string;
};
export type ReadMatterClientPhoneNumberAddedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'matter_client_phone_number_added';
  version: number;
  target_type: 'app.matterclient';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: MatterClientPhoneNumberAddedEvent;
};
export type MatterClientUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  matter_client: MatterClientSchema;
  diff: {
    [key: string]: any;
  };
  previous: MatterClientSchema | null;
};
export type ReadMatterClientUpdatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'matter_client_updated';
  version: number;
  target_type: 'app.matterclient';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: MatterClientUpdatedEvent;
};
export type MatterCompanionMembersAddedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  primary_matter_id: string | string;
  added_member_ids: (string | string)[];
};
export type ReadMatterCompanionMembersAddedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'matter_companion_members_added';
  version: number;
  target_type: 'app.matter';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: MatterCompanionMembersAddedEvent;
};
export type MatterCompanionMembersRemovedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  primary_matter_id: string | string;
  removed_member_ids: (string | string)[];
};
export type ReadMatterCompanionMembersRemovedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'matter_companion_members_removed';
  version: number;
  target_type: 'app.matter';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: MatterCompanionMembersRemovedEvent;
};
export type MatterCompanionPrimarySetEvent = {
  /** PK of the row that changed */
  target_id: string | string;
};
export type ReadMatterCompanionPrimarySetEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'matter_companion_primary_set';
  version: number;
  target_type: 'app.matter';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: MatterCompanionPrimarySetEvent;
};
export type AltAttachmentSchema = {
  id: string;
  name?: string | null;
  content_type?: string | null;
  size?: number | null;
};
export type MessageIdAndAttachmentSchema = {
  message_id: string;
  attachment: AltAttachmentSchema;
};
export type MatterSchema = {
  firm: string | string;
  name?: string | null;
  type?: string | null;
  creator?: string | string | null;
  matter_type?: MatterType;
  status?: MatterStatus | null;
  clients?: MatterClientSchema[];
  front_attachments?: MessageIdAndAttachmentSchema[];
  front_attachment_urls?: string[];
  date_of_loss?: string | null;
  statute_of_limitations_date?: string | null;
  services?: MatterServices[];
  contact_client_directly?: boolean | null;
  city?: string | null;
  state?: string | null;
  representing_attorney?: string | string | null;
  external_cms_url?: string | null;
  referral_source_description?: string | null;
  intake_id?: string | string | null;
  last_contacted_at_override?: string | null;
};
export type MatterCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  matter: MatterSchema;
};
export type ReadMatterCreatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'matter_created';
  version: number;
  target_type: 'app.matter';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: MatterCreatedEvent;
};
export type MatterInvestigationStatus =
  | 'need_info'
  | 'researching'
  | 'investigating'
  | 'escalated'
  | 'completed_report_received'
  | 'closed_no_report'
  | 'not_applicable'
  | 'backfilled_closed';
export type ReadMatterInvestigation = {
  /** Overall investigation status for the matter */
  status?: MatterInvestigationStatus | null;
  /** Free-form notes explaining the current status or transitions */
  description?: string | null;
  id: string | string;
  matter_id: string | string;
  created_at: string;
  modified_at: string;
};
export type MatterInvestigationCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  investigation: ReadMatterInvestigation;
};
export type ReadMatterInvestigationCreatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'matter_investigation_created';
  version: number;
  target_type: 'app.matterinvestigation';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: MatterInvestigationCreatedEvent;
};
export type MatterInvestigationUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  investigation: ReadMatterInvestigation;
  diff: {
    [key: string]: any;
  };
  previous?: ReadMatterInvestigation | null;
};
export type ReadMatterInvestigationUpdatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'matter_investigation_updated';
  version: number;
  target_type: 'app.matterinvestigation';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: MatterInvestigationUpdatedEvent;
};
export type MatterRetainerSignedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  matter: MatterSchema;
};
export type ReadMatterRetainerSignedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'matter_retainer_signed';
  version: number;
  target_type: 'app.matter';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: MatterRetainerSignedEvent;
};
export type MatterRetainerSignedByAttorneyEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  matter: MatterSchema;
};
export type ReadMatterRetainerSignedByAttorneyEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'matter_retainer_signed_by_attorney';
  version: number;
  target_type: 'app.matter';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: MatterRetainerSignedByAttorneyEvent;
};
export type MatterRetainerSignedByClientEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  matter: MatterSchema;
};
export type ReadMatterRetainerSignedByClientEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'matter_retainer_signed_by_client';
  version: number;
  target_type: 'app.matter';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: MatterRetainerSignedByClientEvent;
};
export type MatterStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  matter: MatterSchema;
  old_status: MatterStatus;
  new_status: MatterStatus;
};
export type ReadMatterStatusChangedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'matter_status_changed';
  version: number;
  target_type: 'app.matter';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: MatterStatusChangedEvent;
};
export type MatterUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  matter: MatterSchema;
  diff: {
    [key: string]: any;
  };
  previous?: MatterSchema | null;
};
export type ReadMatterUpdatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'matter_updated';
  version: number;
  target_type: 'app.matter';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: MatterUpdatedEvent;
};
export type NotebookSchema = {
  id: string | string;
  user_id: string | string;
  title: string;
  content: string;
  position: number;
  is_archived: boolean;
  created_at: string;
  modified_at: string;
};
export type NotebookCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  notebook: NotebookSchema;
  user_id: string | string;
  title: string;
};
export type ReadNotebookCreatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'notebook_created';
  version: number;
  target_type: 'app.notebook';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: NotebookCreatedEvent;
};
export type NotebookDeletedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  notebook: NotebookSchema;
  user_id: string | string;
};
export type ReadNotebookDeletedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'notebook_deleted';
  version: number;
  target_type: 'app.notebook';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: NotebookDeletedEvent;
};
export type NotebookMatterLinkedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  notebook: NotebookSchema;
  matter_id: string | string;
  linked_by_id: string | string | null;
};
export type ReadNotebookMatterLinkedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'notebook_matter_linked';
  version: number;
  target_type: 'app.notebook';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: NotebookMatterLinkedEvent;
};
export type NotebookMatterUnlinkedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  notebook: NotebookSchema;
  matter_id: string | string;
  unlinked_by_id: string | string | null;
};
export type ReadNotebookMatterUnlinkedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'notebook_matter_unlinked';
  version: number;
  target_type: 'app.notebook';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: NotebookMatterUnlinkedEvent;
};
export type NotebookUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  notebook: NotebookSchema;
  user_id: string | string;
  diff?: {
    [key: string]: any;
  } | null;
};
export type ReadNotebookUpdatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'notebook_updated';
  version: number;
  target_type: 'app.notebook';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: NotebookUpdatedEvent;
};
export type PartyType = 'person' | 'organization';
export type IdentityStatus = 'named' | 'described' | 'unknown';
export type OrganizationType =
  | 'corporation'
  | 'trust'
  | 'partnership'
  | 'llc'
  | 'llp'
  | 'other';
export type ReadOrganization = {
  party_type?: PartyType;
  is_claimant?: boolean;
  identity_status?: IdentityStatus;
  short_description?: string | null;
  involvement_description?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  legal_name?: string | null;
  dba_name?: string | null;
  organization_type: OrganizationType;
  tax_id?: string | null;
  id: string | string;
  matter_id: string | string;
  created_at: string;
  modified_at: string;
  contact_handles?: ContactHandleSchema[];
  approval_metadata?: EntitySidecar | null;
};
export type OrganizationCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  organization: ReadOrganization;
  needs_approval: boolean;
};
export type ReadOrganizationCreatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'organization_created';
  version: number;
  target_type: 'unknown.unknown';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: OrganizationCreatedEvent;
};
export type OrganizationUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  organization: ReadOrganization;
  previous?: ReadOrganization | null;
};
export type ReadOrganizationUpdatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'organization_updated';
  version: number;
  target_type: 'unknown.unknown';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: OrganizationUpdatedEvent;
};
export type ReadPerson = {
  party_type?: PartyType;
  is_claimant?: boolean;
  identity_status?: IdentityStatus;
  short_description?: string | null;
  involvement_description?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  first_name?: string | null;
  middle_name?: string | null;
  last_name?: string | null;
  suffix?: string | null;
  ssn?: string | null;
  date_of_birth?: string | null;
  stated_age?: number | null;
  is_minor?: boolean;
  gender?: string | null;
  preferred_language?: string | null;
  drivers_license_number?: string | null;
  drivers_license_state_code?: string | null;
  id: string | string;
  matter_id: string | string;
  created_at: string;
  modified_at: string;
  contact_handles?: ContactHandleSchema[];
  approval_metadata?: EntitySidecar | null;
};
export type PersonCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  person: ReadPerson;
  needs_approval: boolean;
};
export type ReadPersonCreatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'person_created';
  version: number;
  target_type: 'unknown.unknown';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: PersonCreatedEvent;
};
export type PersonUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  person: ReadPerson;
  previous?: ReadPerson | null;
};
export type ReadPersonUpdatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'person_updated';
  version: number;
  target_type: 'unknown.unknown';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: PersonUpdatedEvent;
};
export type PoliceReportCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  police_report: ReadPoliceReport;
  needs_approval: boolean;
};
export type ReadPoliceReportCreatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'police_report_created';
  version: number;
  target_type: 'app.policereport';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: PoliceReportCreatedEvent;
};
export type PoliceReportStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  police_report: ReadPoliceReport;
  old_report_status: PoliceReportAttestationStatus;
  new_report_status: PoliceReportAttestationStatus;
};
export type ReadPoliceReportStatusChangedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'police_report_status_changed';
  version: number;
  target_type: 'app.policereport';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: PoliceReportStatusChangedEvent;
};
export type PoliceReportUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  police_report: ReadPoliceReport;
  diff: {
    [key: string]: any;
  };
  previous?: ReadPoliceReport | null;
};
export type ReadPoliceReportUpdatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'police_report_updated';
  version: number;
  target_type: 'app.policereport';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: PoliceReportUpdatedEvent;
};
export type PhysicalAddress = {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
};
export type RealPropertyType =
  | 'residential'
  | 'commercial'
  | 'industrial'
  | 'agricultural'
  | 'mixed_use';
export type ReadProperty = {
  short_description?: string | null;
  involvement_description?: string | null;
  property_name?: string | null;
  physical_address?: PhysicalAddress | null;
  legal_description?: string | null;
  real_property_type: RealPropertyType;
  parcel_id?: string | null;
  id: string | string;
  matter_id: string | string;
  created_at: string;
  modified_at: string;
  approval_metadata?: EntitySidecar | null;
};
export type PropertyCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  property: ReadProperty;
  needs_approval: boolean;
};
export type ReadPropertyCreatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'property_created';
  version: number;
  target_type: 'unknown.unknown';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: PropertyCreatedEvent;
};
export type PropertyUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  property: ReadProperty;
  previous?: ReadProperty | null;
};
export type ReadPropertyUpdatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'property_updated';
  version: number;
  target_type: 'unknown.unknown';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: PropertyUpdatedEvent;
};
export type ProviderCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  provider: MedicalProviderSchema;
  needs_approval: boolean;
};
export type ReadProviderCreatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'provider_created';
  version: number;
  target_type: 'app.medicalprovider';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: ProviderCreatedEvent;
};
export type ProviderCuratedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  provider: MedicalProviderSchema;
  action: string;
  diff?: {
    [key: string]: any;
  } | null;
};
export type ReadProviderCuratedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'provider_curated';
  version: number;
  target_type: 'app.medicalprovider';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: ProviderCuratedEvent;
};
export type ProviderPhoneEnrichedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  provider: MedicalProviderSchema;
  phone_number: string;
  handle_id: number;
};
export type ReadProviderPhoneEnrichedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'provider_phone_enriched';
  version: number;
  target_type: 'app.medicalprovider';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: ProviderPhoneEnrichedEvent;
};
export type ProviderStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  provider: MedicalProviderSchema;
  old_status: ProviderStatus | null;
  new_status: ProviderStatus | null;
};
export type ReadProviderStatusChangedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'provider_status_changed';
  version: number;
  target_type: 'app.medicalprovider';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: ProviderStatusChangedEvent;
};
export type ProviderTreatmentStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  provider: MedicalProviderSchema;
  old_treatment_status: ProviderTreatmentStatus;
  new_treatment_status: ProviderTreatmentStatus;
};
export type ReadProviderTreatmentStatusChangedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'provider_treatment_status_changed';
  version: number;
  target_type: 'app.medicalprovider';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: ProviderTreatmentStatusChangedEvent;
};
export type ProvidersMergedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  provider: MedicalProviderSchema;
  source_provider_ids: (string | string)[];
};
export type ReadProvidersMergedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'providers_merged';
  version: number;
  target_type: 'app.medicalprovider';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: ProvidersMergedEvent;
};
export type TaskSchema = {
  id: string | string;
  matter_id?: string | string | null;
  name: string;
  description: string;
  group: TaskGroups;
  type: TaskType;
  due_date?: string | null;
  medical_note_id?: string | string | null;
  coverage_id?: string | string | null;
  police_report_id?: string | string | null;
  treatment_record_request_id?: string | string | null;
  external_cms_update_status?: TaskExternalCmsUpdateStatus;
  external_cms_update_reason?: string | null;
};
export type RainmakerSuggestionExecutedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task: TaskSchema;
  suggestion_id: string | string;
  suggestion_title: string;
  actions_summary: string[];
};
export type ReadRainmakerSuggestionExecutedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'rainmaker_suggestion_executed';
  version: number;
  target_type: 'app.task';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: RainmakerSuggestionExecutedEvent;
};
export type StaffNoteCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  staff_note: StaffNoteSchema;
  matter_id: string | string;
  content: string;
};
export type ReadStaffNoteCreatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'staff_note_created';
  version: number;
  target_type: 'app.staffnote';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: StaffNoteCreatedEvent;
};
export type StaffNoteDeletedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  staff_note: StaffNoteSchema;
  matter_id: string | string;
};
export type ReadStaffNoteDeletedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'staff_note_deleted';
  version: number;
  target_type: 'app.staffnote';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: StaffNoteDeletedEvent;
};
export type StaffNoteUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  staff_note: StaffNoteSchema;
  matter_id: string | string;
  content: string;
  previous: StaffNoteSchema | null;
};
export type ReadStaffNoteUpdatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'staff_note_updated';
  version: number;
  target_type: 'app.staffnote';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: StaffNoteUpdatedEvent;
};
export type TaskAssignedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task: TaskSchema;
  old?: CustomUserSchema | null;
  new?: CustomUserSchema | null;
};
export type ReadTaskAssignedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'task_assigned';
  version: number;
  target_type: 'app.task';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: TaskAssignedEvent;
};
export type TaskCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task: TaskSchema;
};
export type ReadTaskCreatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'task_created';
  version: number;
  target_type: 'app.task';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: TaskCreatedEvent;
};
export type TaskDeletedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task: TaskSchema;
};
export type ReadTaskDeletedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'task_deleted';
  version: number;
  target_type: 'app.task';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: TaskDeletedEvent;
};
export type TaskExternalCmsUpdateStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task: TaskSchema;
  old: TaskExternalCmsUpdateStatus;
  new: TaskExternalCmsUpdateStatus;
};
export type ReadTaskExternalCmsUpdateStatusChangedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'task_external_cms_update_status_changed';
  version: number;
  target_type: 'app.task';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: TaskExternalCmsUpdateStatusChangedEvent;
};
export type TaskStatus =
  | 'Pending'
  | 'Blocked'
  | 'In Progress'
  | 'Completed'
  | 'Not Applicable'
  | 'Closed';
export type TaskStatusSchema = {
  id: string | string;
  name: string;
  canonical_status: TaskStatus;
  description?: string | null;
};
export type TaskStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task: TaskSchema;
  old?: TaskStatusSchema | null;
  new?: TaskStatusSchema | null;
};
export type ReadTaskStatusChangedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'task_status_changed';
  version: number;
  target_type: 'app.task';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: TaskStatusChangedEvent;
};
export type TaskUpdateCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task_update: TaskUpdateSchema;
};
export type ReadTaskUpdateCreatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'task_update_created';
  version: number;
  target_type: 'app.taskupdate';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: TaskUpdateCreatedEvent;
};
export type TaskUpdateDeletedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task_update: TaskUpdateSchema;
};
export type ReadTaskUpdateDeletedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'task_update_deleted';
  version: number;
  target_type: 'app.taskupdate';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: TaskUpdateDeletedEvent;
};
export type UpdateTaskUpdateSchema = {
  /** The update to the task. This is a free-form text field that can be used to add any relevant information. */
  update?: string | null;
  /** Whether the update is visible to the customer, or internal to the operations staff. */
  visibility?: TaskUpdateVisibility | null;
  /** A list of Blob ids attached to the update. */
  attachments?: (string | string)[] | null;
  /** The user id of the user who created the update. If not provided, the update will be attributed to the system. */
  user_id?: string | string | null;
  agent?: AgentType | null;
  /** The type of update. This is used to determine the type of update to create. */
  update_type?: TaskUpdateType | null;
};
export type TaskUpdateUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task_update: TaskUpdateSchema;
  diff: UpdateTaskUpdateSchema;
  previous?: TaskUpdateSchema | null;
};
export type ReadTaskUpdateUpdatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'task_update_updated';
  version: number;
  target_type: 'app.taskupdate';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: TaskUpdateUpdatedEvent;
};
export type TreatmentCheckInNoteLoggedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  general_notes: string;
};
export type ReadTreatmentCheckInNoteLoggedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'treatment_check_in_note_logged';
  version: number;
  target_type: 'app.treatmentcheckin';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: TreatmentCheckInNoteLoggedEvent;
};
export type TreatmentCheckInStatus =
  | 'DRAFT'
  | 'PROCESSING'
  | 'NEEDS_REVIEW'
  | 'COMPLETED';
export type TreatmentCheckInUrgencyLevel =
  | 'ROUTINE'
  | 'NEEDS_ATTENTION'
  | 'URGENT';
export type TreatmentCheckInProviderUpdateSchema = {
  check_in_id: string | string;
  provider_id?: string | string | null;
  provider_name?: string | null;
  new_treatment_status?: ProviderTreatmentStatus | null;
  new_treatment_status_occurred_at?: string | null;
  new_treatment_frequency?: TreatmentFrequency | null;
  new_treatment_progress?: TreatmentProgress | null;
  new_pain_level?: number | null;
  new_treatment_notes?: string | null;
  id: string | string;
  created_at: string;
  modified_at: string;
};
export type TreatmentCheckInContextSchema = {
  days_since_last_check_in?: number | null;
  days_since_loss?: number | null;
  missing_day_30_diagnostic_testing?: boolean | null;
  missing_last_appointment_dates_count?: number;
  procedure_keywords_detected?: string[];
};
export type TreatmentCheckInSchema = {
  id: string | string;
  created_at: string;
  modified_at: string;
  matter_id: string | string;
  user_id?: string | string | null;
  triggered_by_task_id?: string | string | null;
  triggered_by_task?: any | null;
  status: TreatmentCheckInStatus;
  general_notes: string;
  check_in_time: string | null;
  urgency_level: TreatmentCheckInUrgencyLevel;
  provider_updates?: TreatmentCheckInProviderUpdateSchema[] | null;
  context?: TreatmentCheckInContextSchema | null;
};
export type TreatmentCheckInSubmittedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  check_in: TreatmentCheckInSchema;
};
export type ReadTreatmentCheckInSubmittedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'treatment_check_in_submitted';
  version: number;
  target_type: 'app.treatmentcheckin';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: TreatmentCheckInSubmittedEvent;
};
export type TreatmentRequestCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  treatment_request: TreatmentRecordRequestSchema;
};
export type ReadTreatmentRequestCreatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'treatment_request_created';
  version: number;
  target_type: 'app.treatmentrecordrequest';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: TreatmentRequestCreatedEvent;
};
export type TreatmentRequestDeletedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  treatment_request: TreatmentRecordRequestSchema;
};
export type ReadTreatmentRequestDeletedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'treatment_request_deleted';
  version: number;
  target_type: 'app.treatmentrecordrequest';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: TreatmentRequestDeletedEvent;
};
export type PortalSubmissionChannel = {
  /** Whether this is the preferred submission channel for the provider */
  is_preferred: boolean;
  /** The submission channel is through a medical records request portal (MRO, Smartrequest, Chartswap) */
  channel_type?: 'PORTAL';
  /** The uuid of the portal. */
  portal_id: string;
};
export type FaxContactHandle = {
  /** A contact handle for a fax number */
  handle_type?: 'FAX';
  /** Should contain an E.164 formatted fax number. */
  value: string;
};
export type FaxSubmissionChannel = {
  /** Whether this is the preferred submission channel for the provider */
  is_preferred: boolean;
  /** The submission channel is through a fax number */
  channel_type?: 'FAX';
  /** The handle of the submission channel. E.g. 'fax number' as well as the value */
  handle: FaxContactHandle;
};
export type EmailContactHandle = {
  /** A contact handle for an email address */
  handle_type?: 'EMAIL';
  /** Should contain an email address. */
  value: string;
};
export type EmailSubmissionChannel = {
  /** Whether this is the preferred submission channel for the provider */
  is_preferred: boolean;
  /** The submission channel is through an email address */
  channel_type?: 'EMAIL';
  /** The handle of the submission channel. E.g. 'email address' as well as the value */
  handle: EmailContactHandle;
};
export type MailingAddressContactHandle = {
  /** A contact handle for a mailing address */
  handle_type?: 'MAILING_ADDRESS';
  /** The recipient of the mailing address. */
  recipient: string;
  /** The first line of the mailing address. */
  address_line_1: string;
  /** The second line of the mailing address. */
  address_line_2?: string | null;
  /** The city of the mailing address. */
  city: string;
  /** The state of the mailing address. */
  state: string;
  /** The zip code of the mailing address. */
  zip_code: string;
  /** The country of the mailing address. */
  country: string;
};
export type MailingAddressSubmissionChannel = {
  /** Whether this is the preferred submission channel for the provider */
  is_preferred: boolean;
  /** The submission channel is through a mailing address */
  channel_type?: 'MAILING_ADDRESS';
  /** The handle of the submission channel. E.g. 'mailing address' as well as the value */
  handle: MailingAddressContactHandle;
};
export type WebsiteSubmissionChannel = {
  /** Whether this is the preferred submission channel for the provider */
  is_preferred: boolean;
  /** The submission channel is through a website */
  channel_type?: 'WEBSITE';
  /** The handle of the submission channel. E.g. 'website' as well as the value */
  handle: string;
  /** The url of the website of the submission channel. */
  website: string;
};
export type SubmissionMethod = {
  /** The method by which we can submit records to the provider */
  submission_method:
    | PortalSubmissionChannel
    | FaxSubmissionChannel
    | EmailSubmissionChannel
    | MailingAddressSubmissionChannel
    | WebsiteSubmissionChannel;
  /** A short and concise explanation of why this submission method is a good candidate for requesting records. Write it as you would explain it to a regular person. */
  reason: string;
  /** A score between 0 and 1 indicating the confidence in the submission method  */
  confidence: number;
  /** The scope (records we can expect to receive) for the submission method */
  scope?: TreatmentRecordTypes[] | null;
  /** Any special instructions for submitting records to the provider */
  submission_instructions?: string | null;
};
export type TreatmentRequestResearchCompletedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  provider_id: string | string;
  submission_methods: SubmissionMethod[];
  selected_submission_methods: SubmissionMethod[];
};
export type ReadTreatmentRequestResearchCompletedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'treatment_request_research_completed';
  version: number;
  target_type: 'app.medicalprovider';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: TreatmentRequestResearchCompletedEvent;
};
export type Reason =
  | 'treatment_completed'
  | 'request_submitted_via_portal'
  | 'request_submitted_via_mail'
  | 'request_submitted_via_fax'
  | 'request_submitted_via_email'
  | 'request_updated_via_voice_ai'
  | 'request_updated_via_portal'
  | 'invoice_paid_via_portal'
  | 'records_ingested_via_portal'
  | 'manual';
export type TreatmentRequestStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  treatment_request: TreatmentRecordRequestSchema;
  old_status: TreatmentRecordRequestStatus;
  new_status: TreatmentRecordRequestStatus;
  reason?: Reason | null;
};
export type ReadTreatmentRequestStatusChangedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'treatment_request_status_changed';
  version: number;
  target_type: 'app.treatmentrecordrequest';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: TreatmentRequestStatusChangedEvent;
};
export type UpdateTreatmentRecordRequestSchema = {
  request_id: string | string;
  actor?: TreatmentRecordRequestActor | null;
  is_certified?: boolean | null;
  is_urgent?: boolean | null;
  provided_records?: TreatmentRecordTypes[] | null;
  status?: TreatmentRecordRequestStatus | null;
  metadata?: TreatmentRecordRequestMetadataSchema | null;
};
export type TreatmentRequestUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  treatment_request: TreatmentRecordRequestSchema;
  diff: UpdateTreatmentRecordRequestSchema;
  previous: TreatmentRecordRequestSchema | null;
};
export type ReadTreatmentRequestUpdatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'treatment_request_updated';
  version: number;
  target_type: 'app.treatmentrecordrequest';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: TreatmentRequestUpdatedEvent;
};
export type VehicleType =
  | 'passenger_car'
  | 'truck'
  | 'motorcycle'
  | 'bus'
  | 'bicycle'
  | 'scooter'
  | 'skateboard'
  | 'roller_skate'
  | 'roller_blade'
  | 'trailer'
  | 'other';
export type ReadVehicle = {
  short_description?: string | null;
  involvement_description?: string | null;
  make?: string | null;
  model?: string | null;
  year?: number | null;
  color?: string | null;
  vin?: string | null;
  license_plate?: string | null;
  license_plate_state_code?: string | null;
  vehicle_type: VehicleType;
  estimated_speed_mph?: number | null;
  label_in_police_report?: string | null;
  id: string | string;
  matter_id: string | string;
  created_at: string;
  modified_at: string;
  approval_metadata?: EntitySidecar | null;
};
export type VehicleCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  vehicle: ReadVehicle;
  needs_approval: boolean;
};
export type ReadVehicleCreatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'vehicle_created';
  version: number;
  target_type: 'unknown.unknown';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: VehicleCreatedEvent;
};
export type VehicleUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  vehicle: ReadVehicle;
  previous?: ReadVehicle | null;
};
export type ReadVehicleUpdatedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'vehicle_updated';
  version: number;
  target_type: 'unknown.unknown';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: VehicleUpdatedEvent;
};
export type VoiceAiCallCompletedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  call_id: string;
  task_id?: string | null;
  provider_name?: string | null;
  request_status?: string | null;
  summary?: string | null;
  work_log_entry?: string | null;
};
export type ReadVoiceAiCallCompletedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'voice_ai_call_completed';
  version: number;
  target_type: 'app.treatmentrecordrequest';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: VoiceAiCallCompletedEvent;
};
export type VoiceAgentRunUnblockedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task: TaskSchema;
  previous_status?: string | null;
  new_status?: string | null;
};
export type ReadVoiceAgentRunUnblockedEvent = {
  id: string | string;
  created_at: string;
  modified_at: string;
  occurred_at: string;
  matter: string | string;
  matter_name: string;
  matter_firm_name: string;
  event_code: 'voice_ai_run_unblocked';
  version: number;
  target_type: 'app.task';
  target_id: string | string;
  user?: CustomUserSchema | null;
  agent?: AgentType | null;
  correlation_id?: string | string | null;
  payload: VoiceAgentRunUnblockedEvent;
};
export type ReadDomainEvent =
  | ({
      event_code: 'allocations_replaced';
    } & ReadAllocationsReplacedEvent)
  | ({
      event_code: 'animal_created';
    } & ReadAnimalCreatedEvent)
  | ({
      event_code: 'animal_updated';
    } & ReadAnimalUpdatedEvent)
  | ({
      event_code: 'artifact_created';
    } & ReadArtifactCreatedEvent)
  | ({
      event_code: 'artifact_digest_created';
    } & ReadArtifactDigestCreatedEvent)
  | ({
      event_code: 'artifact_digest_status_updated';
    } & ReadArtifactDigestStatusUpdatedEvent)
  | ({
      event_code: 'blob_created';
    } & ReadBlobCreatedEvent)
  | ({
      event_code: 'blob_deleted';
    } & ReadBlobDeletedEvent)
  | ({
      event_code: 'blob_transform_completed';
    } & ReadMarkdownTransformCompletedEvent)
  | ({
      event_code: 'correspondence_message_transmission_status_changed';
    } & ReadCorrespondenceMessageTransmissionStatusChangedEvent)
  | ({
      event_code: 'correspondence_package_created';
    } & ReadCorrespondencePackageCreatedEvent)
  | ({
      event_code: 'correspondence_package_sent';
    } & ReadCorrespondencePackageSentEvent)
  | ({
      event_code: 'correspondence_package_status_changed';
    } & ReadCorrespondencePackageStatusChangedEvent)
  | ({
      event_code: 'coverage_category_changed';
    } & ReadCoverageCategoryChangedEvent)
  | ({
      event_code: 'coverage_created';
    } & ReadCoverageCreatedEvent)
  | ({
      event_code: 'coverage_modification_blocked';
    } & ReadCoverageModificationBlockedEvent)
  | ({
      event_code: 'coverage_status_changed';
    } & ReadCoverageStatusChangedEvent)
  | ({
      event_code: 'coverage_updated';
    } & ReadCoverageUpdatedEvent)
  | ({
      event_code: 'data_requirement_created';
    } & ReadDataRequirementCreatedEvent)
  | ({
      event_code: 'data_requirement_satisfied';
    } & ReadDataRequirementSatisfiedEvent)
  | ({
      event_code: 'data_requirement_status_changed';
    } & ReadDataRequirementStatusChangedEvent)
  | ({
      event_code: 'data_requirement_value_approved';
    } & ReadDataRequirementValueApprovedEvent)
  | ({
      event_code: 'data_requirement_value_created';
    } & ReadDataRequirementValueCreatedEvent)
  | ({
      event_code: 'data_requirement_value_status_changed';
    } & ReadDataRequirementValueStatusChangedEvent)
  | ({
      event_code: 'deliverable_created';
    } & ReadDeliverableCreatedEvent)
  | ({
      event_code: 'deliverable_match_approved';
    } & ReadDeliverableMatchApprovedEvent)
  | ({
      event_code: 'deliverable_match_created';
    } & ReadDeliverableMatchCreatedEvent)
  | ({
      event_code: 'deliverable_match_status_changed';
    } & ReadDeliverableMatchStatusChangedEvent)
  | ({
      event_code: 'deliverable_satisfied';
    } & ReadDeliverableSatisfiedEvent)
  | ({
      event_code: 'deliverable_status_changed';
    } & ReadDeliverableStatusChangedEvent)
  | ({
      event_code: 'document_created';
    } & ReadDocumentCreatedEvent)
  | ({
      event_code: 'document_deleted';
    } & ReadDocumentDeletedEvent)
  | ({
      event_code: 'document_hipaa_package_generation_audit';
    } & ReadHipaaGenerationAuditEvent)
  | ({
      event_code: 'document_lor_generation_audit';
    } & ReadLoRGenerationAuditEvent)
  | ({
      event_code: 'document_status_changed';
    } & ReadDocumentStatusChangedEvent)
  | ({
      event_code: 'expense_created';
    } & ReadExpenseCreatedEvent)
  | ({
      event_code: 'expense_deleted';
    } & ReadExpenseDeletedEvent)
  | ({
      event_code: 'expense_updated';
    } & ReadExpenseUpdatedEvent)
  | ({
      event_code: 'external_task_rule_evaluation_created';
    } & ReadExternalTaskRuleEvaluationCreatedEvent)
  | ({
      event_code: 'hipaa_authorization_signature_uploaded';
    } & ReadHipaaAuthorizationSignatureUploadedEvent)
  | ({
      event_code: 'incident_created';
    } & ReadIncidentCreatedEvent)
  | ({
      event_code: 'incident_updated';
    } & ReadIncidentUpdatedEvent)
  | ({
      event_code: 'lien_created';
    } & ReadLienCreatedEvent)
  | ({
      event_code: 'lien_deleted';
    } & ReadLienDeletedEvent)
  | ({
      event_code: 'lien_updated';
    } & ReadLienUpdatedEvent)
  | ({
      event_code: 'loa_chaser_campaign_completed';
    } & ReadLoaChaserCampaignCompletedEvent)
  | ({
      event_code: 'loa_chaser_campaign_paused';
    } & ReadLoaChaserCampaignPausedEvent)
  | ({
      event_code: 'loa_chaser_campaign_resumed';
    } & ReadLoaChaserCampaignResumedEvent)
  | ({
      event_code: 'loa_chaser_campaign_started';
    } & ReadLoaChaserCampaignStartedEvent)
  | ({
      event_code: 'loa_chaser_campaign_stopped';
    } & ReadLoaChaserCampaignStoppedEvent)
  | ({
      event_code: 'loa_chaser_manual_activity_logged';
    } & ReadLoaChaserManualActivityLoggedEvent)
  | ({
      event_code: 'loa_chaser_schedule_realigned';
    } & ReadLoaChaserScheduleRealignedEvent)
  | ({
      event_code: 'loa_chaser_snoozed';
    } & ReadLoaChaserSnoozedEvent)
  | ({
      event_code: 'loa_chaser_step_changed';
    } & ReadLoaChaserStepChangedEvent)
  | ({
      event_code: 'loa_chaser_step_executed';
    } & ReadLoaChaserStepExecutedEvent)
  | ({
      event_code: 'lor_data_updated';
    } & ReadLoRDataUpdatedEvent)
  | ({
      event_code: 'matter_client_created';
    } & ReadMatterClientCreatedEvent)
  | ({
      event_code: 'matter_client_phone_number_added';
    } & ReadMatterClientPhoneNumberAddedEvent)
  | ({
      event_code: 'matter_client_updated';
    } & ReadMatterClientUpdatedEvent)
  | ({
      event_code: 'matter_companion_members_added';
    } & ReadMatterCompanionMembersAddedEvent)
  | ({
      event_code: 'matter_companion_members_removed';
    } & ReadMatterCompanionMembersRemovedEvent)
  | ({
      event_code: 'matter_companion_primary_set';
    } & ReadMatterCompanionPrimarySetEvent)
  | ({
      event_code: 'matter_created';
    } & ReadMatterCreatedEvent)
  | ({
      event_code: 'matter_investigation_created';
    } & ReadMatterInvestigationCreatedEvent)
  | ({
      event_code: 'matter_investigation_updated';
    } & ReadMatterInvestigationUpdatedEvent)
  | ({
      event_code: 'matter_retainer_signed';
    } & ReadMatterRetainerSignedEvent)
  | ({
      event_code: 'matter_retainer_signed_by_attorney';
    } & ReadMatterRetainerSignedByAttorneyEvent)
  | ({
      event_code: 'matter_retainer_signed_by_client';
    } & ReadMatterRetainerSignedByClientEvent)
  | ({
      event_code: 'matter_status_changed';
    } & ReadMatterStatusChangedEvent)
  | ({
      event_code: 'matter_updated';
    } & ReadMatterUpdatedEvent)
  | ({
      event_code: 'notebook_created';
    } & ReadNotebookCreatedEvent)
  | ({
      event_code: 'notebook_deleted';
    } & ReadNotebookDeletedEvent)
  | ({
      event_code: 'notebook_matter_linked';
    } & ReadNotebookMatterLinkedEvent)
  | ({
      event_code: 'notebook_matter_unlinked';
    } & ReadNotebookMatterUnlinkedEvent)
  | ({
      event_code: 'notebook_updated';
    } & ReadNotebookUpdatedEvent)
  | ({
      event_code: 'organization_created';
    } & ReadOrganizationCreatedEvent)
  | ({
      event_code: 'organization_updated';
    } & ReadOrganizationUpdatedEvent)
  | ({
      event_code: 'person_created';
    } & ReadPersonCreatedEvent)
  | ({
      event_code: 'person_updated';
    } & ReadPersonUpdatedEvent)
  | ({
      event_code: 'police_report_created';
    } & ReadPoliceReportCreatedEvent)
  | ({
      event_code: 'police_report_status_changed';
    } & ReadPoliceReportStatusChangedEvent)
  | ({
      event_code: 'police_report_updated';
    } & ReadPoliceReportUpdatedEvent)
  | ({
      event_code: 'property_created';
    } & ReadPropertyCreatedEvent)
  | ({
      event_code: 'property_updated';
    } & ReadPropertyUpdatedEvent)
  | ({
      event_code: 'provider_created';
    } & ReadProviderCreatedEvent)
  | ({
      event_code: 'provider_curated';
    } & ReadProviderCuratedEvent)
  | ({
      event_code: 'provider_phone_enriched';
    } & ReadProviderPhoneEnrichedEvent)
  | ({
      event_code: 'provider_status_changed';
    } & ReadProviderStatusChangedEvent)
  | ({
      event_code: 'provider_treatment_status_changed';
    } & ReadProviderTreatmentStatusChangedEvent)
  | ({
      event_code: 'providers_merged';
    } & ReadProvidersMergedEvent)
  | ({
      event_code: 'rainmaker_suggestion_executed';
    } & ReadRainmakerSuggestionExecutedEvent)
  | ({
      event_code: 'staff_note_created';
    } & ReadStaffNoteCreatedEvent)
  | ({
      event_code: 'staff_note_deleted';
    } & ReadStaffNoteDeletedEvent)
  | ({
      event_code: 'staff_note_updated';
    } & ReadStaffNoteUpdatedEvent)
  | ({
      event_code: 'task_assigned';
    } & ReadTaskAssignedEvent)
  | ({
      event_code: 'task_created';
    } & ReadTaskCreatedEvent)
  | ({
      event_code: 'task_deleted';
    } & ReadTaskDeletedEvent)
  | ({
      event_code: 'task_external_cms_update_status_changed';
    } & ReadTaskExternalCmsUpdateStatusChangedEvent)
  | ({
      event_code: 'task_status_changed';
    } & ReadTaskStatusChangedEvent)
  | ({
      event_code: 'task_update_created';
    } & ReadTaskUpdateCreatedEvent)
  | ({
      event_code: 'task_update_deleted';
    } & ReadTaskUpdateDeletedEvent)
  | ({
      event_code: 'task_update_updated';
    } & ReadTaskUpdateUpdatedEvent)
  | ({
      event_code: 'treatment_check_in_note_logged';
    } & ReadTreatmentCheckInNoteLoggedEvent)
  | ({
      event_code: 'treatment_check_in_submitted';
    } & ReadTreatmentCheckInSubmittedEvent)
  | ({
      event_code: 'treatment_request_created';
    } & ReadTreatmentRequestCreatedEvent)
  | ({
      event_code: 'treatment_request_deleted';
    } & ReadTreatmentRequestDeletedEvent)
  | ({
      event_code: 'treatment_request_research_completed';
    } & ReadTreatmentRequestResearchCompletedEvent)
  | ({
      event_code: 'treatment_request_status_changed';
    } & ReadTreatmentRequestStatusChangedEvent)
  | ({
      event_code: 'treatment_request_updated';
    } & ReadTreatmentRequestUpdatedEvent)
  | ({
      event_code: 'vehicle_created';
    } & ReadVehicleCreatedEvent)
  | ({
      event_code: 'vehicle_updated';
    } & ReadVehicleUpdatedEvent)
  | ({
      event_code: 'voice_ai_call_completed';
    } & ReadVoiceAiCallCompletedEvent)
  | ({
      event_code: 'voice_ai_run_unblocked';
    } & ReadVoiceAgentRunUnblockedEvent);
export type ReadDomainEventRead =
  | ({
      event_code: 'allocations_replaced';
    } & ReadAllocationsReplacedEvent)
  | ({
      event_code: 'animal_created';
    } & ReadAnimalCreatedEvent)
  | ({
      event_code: 'animal_updated';
    } & ReadAnimalUpdatedEvent)
  | ({
      event_code: 'artifact_created';
    } & ReadArtifactCreatedEvent)
  | ({
      event_code: 'artifact_digest_created';
    } & ReadArtifactDigestCreatedEvent)
  | ({
      event_code: 'artifact_digest_status_updated';
    } & ReadArtifactDigestStatusUpdatedEvent)
  | ({
      event_code: 'blob_created';
    } & ReadBlobCreatedEvent)
  | ({
      event_code: 'blob_deleted';
    } & ReadBlobDeletedEvent)
  | ({
      event_code: 'blob_transform_completed';
    } & ReadMarkdownTransformCompletedEvent)
  | ({
      event_code: 'correspondence_message_transmission_status_changed';
    } & ReadCorrespondenceMessageTransmissionStatusChangedEvent)
  | ({
      event_code: 'correspondence_package_created';
    } & ReadCorrespondencePackageCreatedEvent)
  | ({
      event_code: 'correspondence_package_sent';
    } & ReadCorrespondencePackageSentEvent)
  | ({
      event_code: 'correspondence_package_status_changed';
    } & ReadCorrespondencePackageStatusChangedEvent)
  | ({
      event_code: 'coverage_category_changed';
    } & ReadCoverageCategoryChangedEvent)
  | ({
      event_code: 'coverage_created';
    } & ReadCoverageCreatedEvent)
  | ({
      event_code: 'coverage_modification_blocked';
    } & ReadCoverageModificationBlockedEvent)
  | ({
      event_code: 'coverage_status_changed';
    } & ReadCoverageStatusChangedEvent)
  | ({
      event_code: 'coverage_updated';
    } & ReadCoverageUpdatedEvent)
  | ({
      event_code: 'data_requirement_created';
    } & ReadDataRequirementCreatedEvent)
  | ({
      event_code: 'data_requirement_satisfied';
    } & ReadDataRequirementSatisfiedEvent)
  | ({
      event_code: 'data_requirement_status_changed';
    } & ReadDataRequirementStatusChangedEvent)
  | ({
      event_code: 'data_requirement_value_approved';
    } & ReadDataRequirementValueApprovedEvent)
  | ({
      event_code: 'data_requirement_value_created';
    } & ReadDataRequirementValueCreatedEvent)
  | ({
      event_code: 'data_requirement_value_status_changed';
    } & ReadDataRequirementValueStatusChangedEvent)
  | ({
      event_code: 'deliverable_created';
    } & ReadDeliverableCreatedEventRead)
  | ({
      event_code: 'deliverable_match_approved';
    } & ReadDeliverableMatchApprovedEventRead)
  | ({
      event_code: 'deliverable_match_created';
    } & ReadDeliverableMatchCreatedEventRead)
  | ({
      event_code: 'deliverable_match_status_changed';
    } & ReadDeliverableMatchStatusChangedEventRead)
  | ({
      event_code: 'deliverable_satisfied';
    } & ReadDeliverableSatisfiedEventRead)
  | ({
      event_code: 'deliverable_status_changed';
    } & ReadDeliverableStatusChangedEventRead)
  | ({
      event_code: 'document_created';
    } & ReadDocumentCreatedEvent)
  | ({
      event_code: 'document_deleted';
    } & ReadDocumentDeletedEvent)
  | ({
      event_code: 'document_hipaa_package_generation_audit';
    } & ReadHipaaGenerationAuditEvent)
  | ({
      event_code: 'document_lor_generation_audit';
    } & ReadLoRGenerationAuditEvent)
  | ({
      event_code: 'document_status_changed';
    } & ReadDocumentStatusChangedEvent)
  | ({
      event_code: 'expense_created';
    } & ReadExpenseCreatedEvent)
  | ({
      event_code: 'expense_deleted';
    } & ReadExpenseDeletedEvent)
  | ({
      event_code: 'expense_updated';
    } & ReadExpenseUpdatedEvent)
  | ({
      event_code: 'external_task_rule_evaluation_created';
    } & ReadExternalTaskRuleEvaluationCreatedEventRead)
  | ({
      event_code: 'hipaa_authorization_signature_uploaded';
    } & ReadHipaaAuthorizationSignatureUploadedEvent)
  | ({
      event_code: 'incident_created';
    } & ReadIncidentCreatedEvent)
  | ({
      event_code: 'incident_updated';
    } & ReadIncidentUpdatedEvent)
  | ({
      event_code: 'lien_created';
    } & ReadLienCreatedEvent)
  | ({
      event_code: 'lien_deleted';
    } & ReadLienDeletedEvent)
  | ({
      event_code: 'lien_updated';
    } & ReadLienUpdatedEvent)
  | ({
      event_code: 'loa_chaser_campaign_completed';
    } & ReadLoaChaserCampaignCompletedEvent)
  | ({
      event_code: 'loa_chaser_campaign_paused';
    } & ReadLoaChaserCampaignPausedEvent)
  | ({
      event_code: 'loa_chaser_campaign_resumed';
    } & ReadLoaChaserCampaignResumedEvent)
  | ({
      event_code: 'loa_chaser_campaign_started';
    } & ReadLoaChaserCampaignStartedEvent)
  | ({
      event_code: 'loa_chaser_campaign_stopped';
    } & ReadLoaChaserCampaignStoppedEvent)
  | ({
      event_code: 'loa_chaser_manual_activity_logged';
    } & ReadLoaChaserManualActivityLoggedEvent)
  | ({
      event_code: 'loa_chaser_schedule_realigned';
    } & ReadLoaChaserScheduleRealignedEvent)
  | ({
      event_code: 'loa_chaser_snoozed';
    } & ReadLoaChaserSnoozedEvent)
  | ({
      event_code: 'loa_chaser_step_changed';
    } & ReadLoaChaserStepChangedEvent)
  | ({
      event_code: 'loa_chaser_step_executed';
    } & ReadLoaChaserStepExecutedEvent)
  | ({
      event_code: 'lor_data_updated';
    } & ReadLoRDataUpdatedEvent)
  | ({
      event_code: 'matter_client_created';
    } & ReadMatterClientCreatedEvent)
  | ({
      event_code: 'matter_client_phone_number_added';
    } & ReadMatterClientPhoneNumberAddedEvent)
  | ({
      event_code: 'matter_client_updated';
    } & ReadMatterClientUpdatedEvent)
  | ({
      event_code: 'matter_companion_members_added';
    } & ReadMatterCompanionMembersAddedEvent)
  | ({
      event_code: 'matter_companion_members_removed';
    } & ReadMatterCompanionMembersRemovedEvent)
  | ({
      event_code: 'matter_companion_primary_set';
    } & ReadMatterCompanionPrimarySetEvent)
  | ({
      event_code: 'matter_created';
    } & ReadMatterCreatedEvent)
  | ({
      event_code: 'matter_investigation_created';
    } & ReadMatterInvestigationCreatedEvent)
  | ({
      event_code: 'matter_investigation_updated';
    } & ReadMatterInvestigationUpdatedEvent)
  | ({
      event_code: 'matter_retainer_signed';
    } & ReadMatterRetainerSignedEvent)
  | ({
      event_code: 'matter_retainer_signed_by_attorney';
    } & ReadMatterRetainerSignedByAttorneyEvent)
  | ({
      event_code: 'matter_retainer_signed_by_client';
    } & ReadMatterRetainerSignedByClientEvent)
  | ({
      event_code: 'matter_status_changed';
    } & ReadMatterStatusChangedEvent)
  | ({
      event_code: 'matter_updated';
    } & ReadMatterUpdatedEvent)
  | ({
      event_code: 'notebook_created';
    } & ReadNotebookCreatedEvent)
  | ({
      event_code: 'notebook_deleted';
    } & ReadNotebookDeletedEvent)
  | ({
      event_code: 'notebook_matter_linked';
    } & ReadNotebookMatterLinkedEvent)
  | ({
      event_code: 'notebook_matter_unlinked';
    } & ReadNotebookMatterUnlinkedEvent)
  | ({
      event_code: 'notebook_updated';
    } & ReadNotebookUpdatedEvent)
  | ({
      event_code: 'organization_created';
    } & ReadOrganizationCreatedEvent)
  | ({
      event_code: 'organization_updated';
    } & ReadOrganizationUpdatedEvent)
  | ({
      event_code: 'person_created';
    } & ReadPersonCreatedEvent)
  | ({
      event_code: 'person_updated';
    } & ReadPersonUpdatedEvent)
  | ({
      event_code: 'police_report_created';
    } & ReadPoliceReportCreatedEvent)
  | ({
      event_code: 'police_report_status_changed';
    } & ReadPoliceReportStatusChangedEvent)
  | ({
      event_code: 'police_report_updated';
    } & ReadPoliceReportUpdatedEvent)
  | ({
      event_code: 'property_created';
    } & ReadPropertyCreatedEvent)
  | ({
      event_code: 'property_updated';
    } & ReadPropertyUpdatedEvent)
  | ({
      event_code: 'provider_created';
    } & ReadProviderCreatedEvent)
  | ({
      event_code: 'provider_curated';
    } & ReadProviderCuratedEvent)
  | ({
      event_code: 'provider_phone_enriched';
    } & ReadProviderPhoneEnrichedEvent)
  | ({
      event_code: 'provider_status_changed';
    } & ReadProviderStatusChangedEvent)
  | ({
      event_code: 'provider_treatment_status_changed';
    } & ReadProviderTreatmentStatusChangedEvent)
  | ({
      event_code: 'providers_merged';
    } & ReadProvidersMergedEvent)
  | ({
      event_code: 'rainmaker_suggestion_executed';
    } & ReadRainmakerSuggestionExecutedEvent)
  | ({
      event_code: 'staff_note_created';
    } & ReadStaffNoteCreatedEvent)
  | ({
      event_code: 'staff_note_deleted';
    } & ReadStaffNoteDeletedEvent)
  | ({
      event_code: 'staff_note_updated';
    } & ReadStaffNoteUpdatedEvent)
  | ({
      event_code: 'task_assigned';
    } & ReadTaskAssignedEvent)
  | ({
      event_code: 'task_created';
    } & ReadTaskCreatedEvent)
  | ({
      event_code: 'task_deleted';
    } & ReadTaskDeletedEvent)
  | ({
      event_code: 'task_external_cms_update_status_changed';
    } & ReadTaskExternalCmsUpdateStatusChangedEvent)
  | ({
      event_code: 'task_status_changed';
    } & ReadTaskStatusChangedEvent)
  | ({
      event_code: 'task_update_created';
    } & ReadTaskUpdateCreatedEvent)
  | ({
      event_code: 'task_update_deleted';
    } & ReadTaskUpdateDeletedEvent)
  | ({
      event_code: 'task_update_updated';
    } & ReadTaskUpdateUpdatedEvent)
  | ({
      event_code: 'treatment_check_in_note_logged';
    } & ReadTreatmentCheckInNoteLoggedEvent)
  | ({
      event_code: 'treatment_check_in_submitted';
    } & ReadTreatmentCheckInSubmittedEvent)
  | ({
      event_code: 'treatment_request_created';
    } & ReadTreatmentRequestCreatedEvent)
  | ({
      event_code: 'treatment_request_deleted';
    } & ReadTreatmentRequestDeletedEvent)
  | ({
      event_code: 'treatment_request_research_completed';
    } & ReadTreatmentRequestResearchCompletedEvent)
  | ({
      event_code: 'treatment_request_status_changed';
    } & ReadTreatmentRequestStatusChangedEvent)
  | ({
      event_code: 'treatment_request_updated';
    } & ReadTreatmentRequestUpdatedEvent)
  | ({
      event_code: 'vehicle_created';
    } & ReadVehicleCreatedEvent)
  | ({
      event_code: 'vehicle_updated';
    } & ReadVehicleUpdatedEvent)
  | ({
      event_code: 'voice_ai_call_completed';
    } & ReadVoiceAiCallCompletedEvent)
  | ({
      event_code: 'voice_ai_run_unblocked';
    } & ReadVoiceAgentRunUnblockedEvent);
export type PaginatedReadDomainEventList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: ReadDomainEvent[];
};
export type PaginatedReadDomainEventListRead = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: ReadDomainEventRead[];
};
